import { appNow, tzParts, tzFormat, getDisplayTz, setDisplayTz, onDisplayTzChange, TZ_OPTIONS } from './utils/appClock'
import ChartMenu, { MenuItem } from './components/menu/ChartMenu'
import { FooterTicker } from './components/DesktopFooter'
import { useMemo, useState, useEffect, useCallback, useRef } from 'react'
import TokenCard from './components/TokenCard'
import { UnifiedWalletProvider, UnifiedWalletButton } from '@jup-ag/wallet-adapter'
import FootprintChart from './components/FootprintChartCanvas'
import { getSOLBalance, searchTokens, normalizeTokenImage } from './utils/jupiter'
import { TradeDefaultsProvider } from './components/TradeDefaultsContext'
import TradeDefaultsMenu from './components/TradeDefaultsMenu'
import WalletHoldings from './components/WalletHoldings'
import ThemePicker from './components/ThemePicker'
import SettingsBridge from './components/SettingsBridge'
import { registerGrokAction } from './utils/grokRegistry'
import useTheme from './components/hooks/useTheme'
import MessagesPanel from './components/MessagesPanel'
import useLobbyChat from './components/hooks/useLobbyChat'
import useFollowAlerts from './components/hooks/useFollowAlerts'
import Workspace from './components/Workspace'
import { useIsPhone, isPhoneDevice } from './components/hooks/useIsPhone'

// Wrapper around FootprintChart that (a) keeps the chart-host div's job —
// arm-on-pointerdown + active/inactive class — and (b) FORWARDS props that
// Workspace injects via cloneElement (coLocated, cellSwitch, headerless) down
// to FootprintChart. Without this forwarding layer those injected props land on
// a plain <div> and are dropped, so the second dropdown never appears and
// chart2 never goes headerless. `hostActive`/`onArm` drive the wrapper; the
// rest spread onto the chart.
// Isolated 1Hz clock. Owning its own state means a tick re-renders THIS
// component and nothing else — App is untouched. It also stores the FORMATTED
// STRING rather than a Date: the label only changes once a minute, so 59 of
// every 60 ticks set an identical string and React bails out without
// rendering at all. Same display, ~1 render per minute instead of a full-app
// render per second.
//
// Format is assembled from getHours()/getMinutes() rather than
// toLocaleTimeString for the reason documented at the call site: en-US emits a
// NARROW NO-BREAK SPACE (U+202F) before AM/PM on newer ICU, so stripping a
// plain space silently no-ops on some engines.
function formatClock(ms, sec = false) {
  const d = tzParts(ms)   // server time, in the chosen display zone
  return `${d.h % 12 || 12}:${String(d.m).padStart(2, '0')}${sec ? ':' + String(d.s).padStart(2, '0') : ''}${d.h >= 12 ? 'PM' : 'AM'}`
}

// Right-click the clock -> pick the display time zone (labels only; candles
// always bucket on server epoch time).
function FooterClock({ seconds = false, showTz = true }) {
  const [label, setLabel] = useState(() => formatClock(appNow(), seconds))
  const [tz, setTz] = useState(getDisplayTz)
  const [menu, setMenu] = useState(null)
  useEffect(() => {
    const tick = () => setLabel(formatClock(appNow(), seconds))
    const timer = setInterval(tick, 1000)
    const off = onDisplayTzChange((z) => { setTz(z); tick() })
    return () => { clearInterval(timer); off() }
  }, [seconds])
  const short = tz === 'local' ? '' : ' ' + (tzFormat({ timeZoneName: 'short' }, 'en-US').formatToParts(new Date(appNow())).find(p => p.type === 'timeZoneName')?.value || '')
  return (
    <>
      <span title="Server time. Right-click for time zone" style={{ cursor: 'context-menu' }}
        onContextMenu={(e) => { e.preventDefault(); setMenu({ x: e.clientX, y: e.clientY }) }}>
        {label}{showTz ? short : ''}
      </span>
      {menu && (
        <ChartMenu anchor={menu} onClose={() => setMenu(null)} stopMouseDown closeOnContextMenu className="cm-list" title="Time zone">
          {TZ_OPTIONS.map(o => (
            <MenuItem key={o.id} onClick={() => { setDisplayTz(o.id); setMenu(null) }}>
              {(tz === o.id ? '\u2713 ' : '\u2003') + o.label}
            </MenuItem>
          ))}
        </ChartMenu>
      )}
    </>
  )
}

function ChartHost({ hostActive, onArm, coLocated = false, cellSwitch = null, secondChart = null, headerless = false, ...chartProps }) {
  return (
    <div
      className={`chart-host${hostActive ? ' chart-host-active' : ' chart-host-inactive'}`}
      onPointerDownCapture={onArm}
    >
      <FootprintChart
        {...chartProps}
        coLocated={coLocated}
        cellSwitch={cellSwitch}
        secondChart={secondChart}
        headerless={headerless}
      />
    </div>
  )
}
import TapePanel from './components/TapePanel'
import WalletStatsPanel from './components/WalletStatsPanel'
import WalletsPanel from './components/WalletsPanel'
import { WalletAlertNotifications } from './components/PriceAlertUI'
import { openWalletMap } from './utils/walletSources'
import WalletMapPanel from './components/WalletMapPanel'
import JournalPanel from './components/JournalPanel'
import PortfolioPanel from './components/PortfolioPanel'   // portfolio-20261027
import NotesPanel from './components/NotesPanel'
import CalendarPanel from './components/CalendarPanel'
import WatchlistPanel from './components/WatchlistPanel'
import ScanPanel from './components/ScanPanel'
import useTradeHistory from './components/hooks/useTradeHistory'
import useHlFills from './components/hooks/useHlFills'
import { formatCompact, CME_FUTURES, cmeInfo, STOCKS, stkInfo, stkCoin } from './utils/chartUtils'
import OptionsChainPanel from './components/OptionsChainPanel'
import { initSettingsSync, queuePush } from './utils/settingsSync'
import { loadXWin } from './chart/machines/xZoomStore'
import Toast from './components/Toast'
import { LocalWalletProvider } from './components/WalletContext'
import { DualWalletProvider, useDualWallet, WALLET_MODE } from './components/DualWalletContext'
import WalletSetup from './components/WalletSetup'
import { PrivyWalletButton } from './components/PrivyWalletButton'
import { HyperliquidWalletProvider, useHyperliquidWallet } from './components/Hyperliquid/HyperliquidWalletContext'
import HyperliquidWalletSetup from './components/Hyperliquid/HyperliquidWalletSetup'
import SchwabAccountBadge from './components/SchwabAccountBadge'
import HyperliquidFuturesSelect from './components/Hyperliquid/HyperliquidFuturesSelect'
import useChartSlot from './components/hooks/useChartSlot'
import { TradingProvider } from './components/TradingContext'
import { useTradeDefaults } from './components/TradeDefaultsContext'
// (hlxyz) HIP-3 coins are 'dex:COIN' - dex stays lowercase, as HL keys it.
const hlCoinNorm = (s) => { const v = String(s || '').trim(); if (/^STK(-OPT)?:/i.test(v)) return stkCoin(v); const k = v.indexOf(':'); return k > 0 ? v.slice(0, k).toLowerCase() + ':' + v.slice(k + 1).toUpperCase() : v.toUpperCase() }
import { useTypewriterPaste, typewriterType } from './components/hooks/useTypewriterPaste'
import { dbg } from './utils/debugFlag'
import HealthPanel from './components/HealthPanel'   // health-20261104

// Compact FDV/market-cap formatter used by the Solana token search rows.
// Module-scope so it isn't reallocated per render. Returns '—' for missing
// or non-finite values so the right-aligned column doesn't shift width
// between rows with/without numbers.
function fmtFdv(n) {
  if (n == null || !isFinite(n)) return '—'
  if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`
  if (n >= 1e6) return `$${(n / 1e6).toFixed(2)}M`
  if (n >= 1e3) return `$${(n / 1e3).toFixed(1)}K`
  return `$${n.toFixed(0)}`
}

// useIsPhone now lives in components/hooks/useIsPhone and asks the DEVICE, not
// the window — see that file. The local copy here was a live (max-width: 768px)
// subscription, which meant turning the phone to landscape (~852px) swapped the
// header out from under the two-tap connect flow mid-session. The CSS asks the
// same question now (html.is-phone), so the two can't disagree.

function WalletBalance() {
  const { publicKey } = useDualWallet()
  const [balance, setBalance] = useState(null)

  useEffect(() => {
    if (!publicKey) {
      setBalance(null)
      return
    }

    // A failed read keeps the last good balance (was: blank it).
    const fetchBalance = () => {
      getSOLBalance(publicKey).then(setBalance).catch(() => {})
    }

    // Same staged refresh as the trade station: RPC reads lag a landed tx,
    // so re-read at 0/1.5/4/10s after any trade by this wallet (own trades
    // + the server's wallet-activity feed). A new trade restarts the stages.
    let stages = []
    const clearStages = () => { stages.forEach(clearTimeout); stages = [] }
    const refreshAfterTx = () => {
      clearStages()
      stages = [0, 1500, 4000, 10000].map(ms => setTimeout(fetchBalance, ms))
    }
    const evs = ['solcheddar-buy-executed', 'solcheddar-sell-executed',
                 'solcheddar-position-closed', 'solcheddar-wallet-position-updated']
    evs.forEach(e => window.addEventListener(e, refreshAfterTx))

    const initialDelay = setTimeout(fetchBalance, 5000)
    const interval = setInterval(fetchBalance, 30000)
    return () => {
      clearTimeout(initialDelay); clearInterval(interval); clearStages()
      evs.forEach(e => window.removeEventListener(e, refreshAfterTx))
    }
  }, [publicKey])

  if (balance === null) return null

  return (
    <span className="wallet-balance">{balance.toFixed(4)} SOL</span>
  )
}

function WalletModeToggle({ collapsed = false, onToggleCollapsed }) {
  const { mode, setMode, isReady } = useDualWallet()
  // Collapsed: the two idle options SLIDE BEHIND the active one (CSS
  // .wallet-mode-toggle.collapsed — width/opacity transition), leaving the
  // connected diamond + the ready indicator visible. Clicking the active
  // button while collapsed expands instead of re-setting the mode; while
  // expanded it collapses again. Callers that never pass props (PhoneConnect)
  // get the classic always-open toggle unchanged.
  const btn = (m, img, alt, cls, title) => (
    <button
      className={`wmt-btn ${mode === m ? 'active is-active' : 'is-idle'}`}
      onClick={() => (collapsed || mode === m) && onToggleCollapsed ? onToggleCollapsed() : setMode(m)}
      title={title}
    >
      <img src={img} alt={alt} className={cls} />
    </button>
  )
  return (
    <div className={`wallet-mode-toggle${collapsed ? ' collapsed' : ''}`}>
      {btn(WALLET_MODE.BROWSER, '/globe.png', 'globe logo', 'globe-logo', 'Browser wallet (Phantom, Solflare) — with confirmations')}
      {btn(WALLET_MODE.LOCAL, '/key.png', 'key logo', 'key-logo', 'Local wallet — instant, no confirmations')}
      {btn(WALLET_MODE.PRIVY, '/cheese.png', 'bot wallet logo', 'cheese-logo', 'Bot wallet — email login, user-owned, exportable')}
      <span className={`wmt-indicator ${isReady ? 'ready' : 'not-ready'}`} />
    </div>
  )
}

function HyperliquidBalance() {
  const [balance, setBalance] = useState(null)
  const { hasStoredKeys, isLocked } = useHyperliquidWallet()

  useEffect(() => {
    const handler = (e) => setBalance(e.detail)
    window.addEventListener('solcheddar-hl-balance', handler)
    return () => window.removeEventListener('solcheddar-hl-balance', handler)
  }, [])

  if (!hasStoredKeys || isLocked) return null

  const available = balance ? parseFloat(balance.available) || 0 : null

  return (
    <span className="wallet-balance" style={{ color: '#46d41f' }}>
      {available !== null
        ? (available >= 1000 ? '$' + (available / 1000).toFixed(1) + 'K' : '$' + available.toFixed(2))
        : '...'
      }
    </span>
  )
}

// Phone two-tap connect: tap Connect → the globe/key mode toggle slides down
// from under the button (pick browser vs local), stays open until you connect
// or tap elsewhere; tap Connect again to actually connect with the chosen mode.
// Renders only on phone (WalletArea branches on isPhone). Desktop keeps the
// flat always-visible wallet row.
function PhoneConnect() {
  const { mode, publicKey } = useDualWallet()
  // `armed`: user tapped Connect at least once → show the real connect control
  // (WalletSetup / UnifiedWalletButton) instead of the plain Connect button.
  // `slideOpen`: whether the globe/key mode toggle is dropped down. SEPARATE
  // states: the second tap retracts the slide but must NOT unmount the control.
  // WalletSetup holds its own unlock/import dropdown state internally — when
  // the slide-close was tied to `armed` (which gated the control's mount),
  // tapping to connect unmounted WalletSetup on the same click and destroyed
  // that state before the unlock could appear ("unlock doesn't come up").
  const [armed, setArmed] = useState(false)
  const [slideOpen, setSlideOpen] = useState(false)
  const rootRef = useRef(null)
  const connected = !!publicKey

  useEffect(() => {
    if (connected) setSlideOpen(false)
  }, [connected])

  // Tap-elsewhere closes the slide and (if not connected) disarms back to the
  // plain Connect button. Deferred so the opening tap doesn't self-close.
  useEffect(() => {
    if (!slideOpen && !armed) return
    const onOutside = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setSlideOpen(false)
        if (!connected) setArmed(false)
      }
    }
    const t = setTimeout(() => {
      document.addEventListener('mousedown', onOutside)
      document.addEventListener('touchstart', onOutside)
    }, 0)
    return () => {
      clearTimeout(t)
      document.removeEventListener('mousedown', onOutside)
      document.removeEventListener('touchstart', onOutside)
    }
  }, [slideOpen, armed, connected])

  const showSlide = slideOpen && !connected

  return (
    <div className="phone-connect" ref={rootRef} style={{ position: 'relative' }}>
      {connected || armed ? (
        // Real connect control stays mounted once armed. The SECOND tap lands
        // here: it retracts the slide (setSlideOpen(false)) while its own click
        // bubbles to WalletSetup/UnifiedWalletButton to open the unlock/import
        // dropdown (or Jupiter's modal). Control is NOT unmounted, so that
        // dropdown can appear.
        <div
          className={`phone-connect-btn phone-connect-armed-wrap ${mode === WALLET_MODE.BROWSER ? 'pc-browser' : mode === WALLET_MODE.PRIVY ? 'pc-privy' : 'pc-local'}${connected ? ' is-connected' : ''}`}
          onClick={() => setSlideOpen(false)}
        >
          {mode === WALLET_MODE.BROWSER
            ? <span className={`wallet-connect-slot${connected ? ' is-connected' : ''}`}><UnifiedWalletButton /></span>
            : mode === WALLET_MODE.PRIVY
            ? <PrivyWalletButton />
            : <WalletSetup />}
        </div>
      ) : (
        <button
          className="phone-connect-btn"
          onClick={() => { setArmed(true); setSlideOpen(true) }}
        >Connect</button>
      )}

      {/* Globe/key mode toggle slides down while the slide is open & not yet
          connected. */}
      <div className={`phone-mode-slide${showSlide ? ' open' : ''}`}>
        <WalletModeToggle />
      </div>
    </div>
  )
}

function WalletArea({ stkRelayUp, dataSource, isFutures, hlPair, data, stationOpen, setStationOpen, stationCloseReq }) {
  const { mode, publicKey } = useDualWallet()
  const connected = !!publicKey
  // Collapse the mode toggle behind the active connection. Not three states
  // to branch on — one derived fact (connected) and one boolean (expanded).
  // The active mode's connect component already renders exclusively below;
  // all this governs is whether the 3-way toggle or a compact expander
  // shows beside it. Auto-collapses whenever a connection lands.
  const [modeToggleOpen, setModeToggleOpen] = useState(false)
  useEffect(() => { if (connected) setModeToggleOpen(false) }, [connected])
  const isPhone = useIsPhone()
  const [toast, setToast] = useState({ message: '', type: '', show: false })

  const showToast = useCallback((message, type) => {
    setToast({ message, type, show: true })
    // trade-errlog-20261019: failures stay up 12s (also logged to messages).
    setTimeout(() => setToast(t => ({ ...t, show: false })), /fail/i.test(String(message)) ? 12000 : type === 'sell' ? 5000 : 3000)
  }, [])

  const isStk = /^STK(-OPT)?:/.test(hlPair?.base || '')   // schwabbadge-20261104
  return (
    <div className="wallet">
      {isFutures ? (
        <>
          {/* Balance hidden on phone to save header space. */}
          {!isPhone && !isStk && <HyperliquidBalance />}
          <WalletHoldings showToast={showToast} dataSource="hyperliquid" hlPair={hlPair} data={data} open={stationOpen} setOpen={setStationOpen} closeRequest={stationCloseReq} />
          {!isStk && <TradeDefaultsMenu />}
          {isStk ? <SchwabAccountBadge relayUp={stkRelayUp} /> : <HyperliquidWalletSetup />}
        </>
      ) : isPhone ? (
        <>
          {/* Phone Solana: hide balance, order defaults, and the always-visible
              globe/key toggle. WalletHoldings (the trade station) stays. Connect
              is the two-tap PhoneConnect with the sliding mode toggle. */}
          <WalletHoldings showToast={showToast} data={data} open={stationOpen} setOpen={setStationOpen} closeRequest={stationCloseReq} />
          <PhoneConnect />
        </>
      ) : (
        <>
          <WalletBalance />
          <WalletHoldings showToast={showToast} data={data} open={stationOpen} setOpen={setStationOpen} closeRequest={stationCloseReq} />
          <TradeDefaultsMenu />
          <WalletModeToggle
            collapsed={connected && !modeToggleOpen}
            onToggleCollapsed={() => setModeToggleOpen(o => !o)}
          />
          {mode === WALLET_MODE.BROWSER && (
            <div className={`wallet-connect-slot${connected ? ' is-connected' : ''}`}>
              <UnifiedWalletButton />
            </div>
          )}
          {mode === WALLET_MODE.LOCAL && <WalletSetup />}
          {mode === WALLET_MODE.PRIVY && <PrivyWalletButton />}
        </>
      )}
      <Toast message={toast.message} type={toast.type} show={toast.show} />
    </div>
  )
}

function AppContent() {
  const isPhone = useIsPhone()   // footer-20261031: desktop/phone footer split
  // Whole-app browser fullscreen (footer Expand button).
  const [isFullscreen, setIsFullscreen] = useState(() => typeof document !== 'undefined' && !!document.fullscreenElement)
  useEffect(() => {
    const onFs = () => setIsFullscreen(!!document.fullscreenElement)
    document.addEventListener('fullscreenchange', onFs)
    return () => document.removeEventListener('fullscreenchange', onFs)
  }, [])
  const toggleFullscreen = useCallback(() => {
    try {
      if (document.fullscreenElement) document.exitFullscreen?.()?.catch?.(() => {})
      else document.documentElement.requestFullscreen?.()?.catch?.(() => {})
    } catch { /* fullscreen unsupported */ }
  }, [])
  // ─── Global IPFS image recovery (delegated, capture-phase) ───
  // The jupiter.js normalizer heals icons entering via SEARCH, but token
  // images also arrive from the ws scan feed, watchlist storage, ticker
  // payloads — many doors, one failure mode. This listener is the net under
  // all of them: any failing IPFS <img>, wherever it rendered from, gets
  // rewritten ONCE onto our own proxy. Content-addressed URLs make that safe
  // — same CID, same bytes.
  //
  // The retry target is now /api/ipfs/ rather than ipfs.io. ipfs.io was the
  // fallback until it started blocking cross-origin embedding outright
  // (ERR_BLOCKED_BY_RESPONSE.NotSameOrigin), which turned the safety net into
  // the failure — every retry landed on the one host guaranteed to refuse.
  // The proxy fetches server-side and walks a gateway chain, so it can't be
  // taken out by one host's hotlink policy.
  useEffect(() => {
    const onImgError = (e) => {
      const t = e.target
      if (!t || t.tagName !== 'IMG' || t.dataset.ipfsRetried) return
      try {
        const u = new URL(t.src, window.location.origin)
        const m = u.pathname.match(/^\/ipfs\/(.+)$/)
        if (!m) return
        // Already proxied and still failing: every gateway missed. Let the
        // placeholder show rather than looping on our own origin.
        if (u.pathname.startsWith('/api/ipfs/')) return
        t.dataset.ipfsRetried = '1'
        t.src = '/api/ipfs/' + m[1] + u.search
      } catch {}
    }
    window.addEventListener('error', onImgError, true)   // capture: img errors don't bubble
    return () => window.removeEventListener('error', onImgError, true)
  }, [])

  // ─── Wheel-adjust for number inputs, app-wide ───
  // Companion to the CSS that kills the hover spinners: hover any
  // input[type=number] and roll to step it (respects min/max/step, no focus
  // needed). Two subtleties: the listener must be non-passive to stop the
  // page scrolling under the panel, and the value must go through the
  // NATIVE setter + a bubbled 'input' event — assigning .value directly
  // bypasses React's onChange and the setting would never persist.
  useEffect(() => {
    const nativeSet = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value')?.set
    const onWheel = (e) => {
      const el = e.target
      if (!el || el.tagName !== 'INPUT' || el.type !== 'number' || el.disabled || !nativeSet) return
      e.preventDefault()
      const step = parseFloat(el.step) || 1
      const min = el.min !== '' ? parseFloat(el.min) : -Infinity
      const max = el.max !== '' ? parseFloat(el.max) : Infinity
      const cur = parseFloat(el.value) || 0
      const decimals = (String(step).split('.')[1] || '').length
      const next = Math.max(min, Math.min(max, cur + (e.deltaY < 0 ? step : -step)))
      nativeSet.call(el, next.toFixed(decimals))
      el.dispatchEvent(new Event('input', { bubbles: true }))
    }
    window.addEventListener('wheel', onWheel, { passive: false, capture: true })
    return () => window.removeEventListener('wheel', onWheel, { passive: false, capture: true })
  }, [])

  // Trade-station open/closed, LIFTED out of WalletHoldings so the footer can
  // reopen it. It has to live here: WalletHoldings mounts inside WalletArea (the
  // header), the reopen button lives in the footer, and they're siblings — no
  // common ancestor below this one.
  //
  // This closes a real trap on phone: the station's popup opens by default, but
  // .wallet-logo (its toggle) is `display: none !important` there, so hitting ✕
  // set open=false with NOTHING left on screen able to set it back. The station
  // was gone until reload. The footer button is the way back.
  // Phone opens CLOSED. On desktop the station is a column beside the chart and
  // costs nothing to leave open; on a phone it's a sheet over the chart, so
  // defaulting it open means every session starts by dismissing something to
  // see the market. The footer button reopens it in one tap.
  // isPhoneDevice() (not the hook) because this is a lazy initializer — it runs
  // once at mount, and the device answer is constant for the tab anyway.
  const [stationOpen, setStationOpen] = useState(() => !isPhoneDevice())
  // The footer button can't close the station directly. startClose() — the
  // collapse-then-fold — lives inside WalletHoldings, and this button is its
  // sibling, not its parent. So the footer signals an INTENT (a bump) rather
  // than flipping the state, and the panel turns that into the same fold the ✕
  // plays. Flipping stationOpen straight to false skipped the animation, and
  // also never armed the panel's justClosedRef — so the REOPEN skipped the
  // unfold as well. Opening is still a plain setState: there's nothing to
  // choreograph on the way in that the panel can't do for itself.
  const [stationCloseReq, setStationCloseReq] = useState(0)
  // Footer status → slide-up card for the charted token.
  const [tokenCardOpen, setTokenCardOpen] = useState(false)
  const [healthAt, setHealthAt] = useState(null)   // health-20261104: lamp popover anchor
  const openHealth = (e) => { e.stopPropagation(); setHealthAt(h => (h ? null : { x: e.clientX, y: e.clientY - 8 })) }
  const { publicKey, signTransaction } = useDualWallet()
  const hlWallet = useHyperliquidWallet()
  const { defaults } = useTradeDefaults()

  // Theme is owned at the app level so the footer's ThemePicker can drive it
  // and it can apply app-wide; `theme` is passed down to the chart's draw loop.
  const {
    theme, themeId, setThemeId,
    themeOverrides, setThemeColor, resetThemeColor, resetThemeColors,
    themesVersion, addTheme, deleteTheme,
  } = useTheme()

  // Live clock moved OUT of App — see <FooterClock/>. It lived here as App
  // state and was read in exactly one place (the footer), so every tick
  // re-rendered the ENTIRE App tree once per second: every panel, the ticker,
  // the footer, both chart hosts. Measured as a 60-100ms React stall at ~1Hz —
  // the visible "tick...tick...tick" rhythm in the orderflow trail.
  
  // --- Chart Data & UI State ---
  const [tokenInput, setTokenInput] = useState('')
  const [showTokenHistory, setShowTokenHistory] = useState(false)
  // Phone only: the search bar is hidden and slides down from the top as an
  // overlay when the "Cheddar Terminal" logo is tapped. No effect on desktop —
  // the slide transform lives entirely under html.is-phone, and off a phone the
  // search is always visible regardless of this flag.
  const [searchSlideOpen, setSearchSlideOpen] = useState(false)
  // Slide search auto-dismiss: (1) a timer closes it after a few idle seconds,
  // (2) clicking/tapping anywhere outside the search overlay closes it. The
  // logo toggle and token-pick handlers cover the other dismiss paths. The
  // outside-click guard excludes .above-chart (the overlay itself) and the
  // logo <h1> so neither self-closes. The timer is reset on each open.
  useEffect(() => {
    if (!searchSlideOpen) return
    // Dismiss on outside click/tap or pair select only — no idle timer.
    // (Removed the 6s auto-close; the search stays open until the user
    // clicks away or picks a pair.)
    const onOutside = (e) => {
      const t = e.target
      if (t.closest && (t.closest('.above-chart') || t.closest('.header h1'))) return
      setSearchSlideOpen(false)
    }
    // Defer the listener so the opening logo tap doesn't immediately close it.
    const armed = setTimeout(() => {
      document.addEventListener('mousedown', onOutside)
      document.addEventListener('touchstart', onOutside)
    }, 0)
    return () => {
      clearTimeout(armed)
      document.removeEventListener('mousedown', onOutside)
      document.removeEventListener('touchstart', onOutside)
    }
  }, [searchSlideOpen])
  const [showFuturesDropdown, setShowFuturesDropdown] = useState(false)
  const inputWrapperRef = useRef(null)
  // Guards Enter-to-subscribe while the typewriter animation is running
  const typewriterActiveRef = useRef(false)
  
  // dataSource/hlPair moved into the chart slots (per-slot mode) —
  // App-level bindings are DERIVED from the active slot below, so every
  // existing consumer (header, satellites, trading) follows the armed
  // chart, while each chart's props come from its own slot.
  const dataSourceRef = useRef('solana')

  // Futures pairs state
  const [hlPairs, setHlPairs] = useState([])
  const [futTab, setFutTab] = useState('crypto')   // FUTURES dropdown: 'crypto' | 'cme' | 'stk'
  const [optChainFor, setOptChainFor] = useState(() => { try { return localStorage.getItem('cheddar_opt_underlying') || 'SPY' } catch { return 'SPY' } })   // schwab-20261103: underlying for the Options panel (current or last stock)
  const [stkRelayUp, setStkRelayUp] = useState(true)
  const [hlRecentPairs, setHlRecentPairs] = useState(() => {
    try { return JSON.parse(localStorage.getItem('hl_recent_pairs')) || [] } catch { return [] }
  })
  
  const publicKeyRef = useRef(null)
  useEffect(() => { publicKeyRef.current = publicKey ? publicKey.toString() : null }, [publicKey])

  // Toast for App-level notifications
  const [appToast, setAppToast] = useState({ message: '', type: '', show: false })
  const showAppToast = useCallback((message, type) => {
    setAppToast({ message, type, show: true })
    setTimeout(() => setAppToast(t => ({ ...t, show: false })), 3000)
  }, [])

  // ─── Chart slot: per-instance settings + data connection as one unit ───
  // Stage 1 of multi-chart (see useChartSlot.js): one slot, destructured
  // flat so every existing reference below compiles unchanged. Stage 2
  // adds slot 'B' + activeChartId, and the satellite wiring (tape,
  // trading, mint-click routing) reads from the ACTIVE slot instead of
  // these bindings.
  const slot = useChartSlot('main', { publicKeyRef, showToast: showAppToast })
  const {
    data,
    timeInterval, setTimeInterval,
    mcapBucketSize, setMcapBucketSize,
    autoScrollEnabled, setAutoScrollEnabled,
    setViewWindowSize,
    xWin,
    bodyScrollRef,
    timeIntervalRef,
  } = slot

  // ─── Stage 2: second chart slot + focus routing ───
  // Both sockets stay hot; focus is a POINTER, never a resubscribe.
  // activeData feeds everything that should follow the armed chart:
  // trading, tape, wallet holdings, watchlist-current, and every
  // mint-click (scan/wl/chat/header). Deliberately NOT focus-routed:
  // chat + boost feed + HL pairs plumbing + footer status (main socket,
  // always-on), the bottom ticker (main chart only), and futures mode —
  // dataSource/hlPair are global, so v1 futures lives on the main chart;
  // mixed solana+futures across charts is a deferred cut.
  const slotB = useChartSlot('B', { publicKeyRef, showToast: showAppToast })
  const [activeChartId, setActiveChartId] = useState('main')
  const activeSlot = activeChartId === 'B' ? slotB : slot
  const activeData = activeSlot.data

  // Memoized sibling descriptors. These are passed to FootprintChart as
  // `secondChart`; as inline object literals they got a fresh identity on
  // every App render, which (together with `data` and `onHistorySelect`)
  // forced a full ~25ms chart re-render even when nothing had changed.
  // Recomputed only when something actually displayed here moves.
  const shortMint = (m) => (m ? `${m.slice(0, 4)}…${m.slice(-4)}` : 'Cheddar')
  const secondChartForA = useMemo(() => ({
    siblingKey: 'chart2',
    siblingSide: 'right',
    label: slotB.dataSource === 'hyperliquid' && slotB.hlPair
      ? `${slotB.hlPair.base}/USDC`
      : shortMint(slotB.data.currentToken),
    isActive: activeChartId === 'B',
    onActivate: () => setActiveChartId('B'),
  }), [slotB.dataSource, slotB.hlPair, slotB.data.currentToken, activeChartId])
  const secondChartForB = useMemo(() => ({
    siblingKey: 'chart',
    siblingSide: 'left',
    label: slot.dataSource === 'hyperliquid' && slot.hlPair
      ? `${slot.hlPair.base}/USDC`
      : shortMint(slot.data.currentToken),
    isActive: activeChartId === 'main',
    onActivate: () => setActiveChartId('main'),
  }), [slot.dataSource, slot.hlPair, slot.data.currentToken, activeChartId])
  // Active-slot mode, under the names the rest of App already uses.
  const dataSource = activeSlot.dataSource
  const setDataSource = activeSlot.setDataSource
  const hlPair = activeSlot.hlPair
  useEffect(() => {
    if (futTab !== 'stk' && !stkInfo(hlPair?.base) && !optChainFor) return
    let dead = false
    const poll = () => fetch(`${import.meta.env.VITE_HTTP_PROXY_BASE || 'https://history.cheddarterminal.app'}/stk/status`)
      .then(r => r.json()).then(j => { if (!dead) setStkRelayUp(!!j.relay) }).catch(() => { if (!dead) setStkRelayUp(false) })
    poll(); const t = setInterval(poll, 20000)
    return () => { dead = true; clearInterval(t) }
  }, [futTab, hlPair?.base, optChainFor])
  const setHlPair = activeSlot.setHlPair
  // opt-open-20261103: open/focus the Options panel on a given underlying.
  // Also reachable via window event 'cheddar-open-options' {detail:{symbol}}.
  const openOptionsFor = useCallback((sym) => {
    const raw = String(sym || ''); const u = (raw.startsWith('STK-OPT:') ? raw.slice(8, 14) : raw.replace(/^STK:/, '')).trim().toUpperCase()
    if (!u) return
    setOptChainFor(u); try { localStorage.setItem('cheddar_opt_underlying', u) } catch {}
    window.dispatchEvent(new CustomEvent('cheddar-open-panel', { detail: { panel: 'opt' } }))
  }, [])
  useEffect(() => {
    const h = (e) => openOptionsFor(e?.detail?.symbol)
    window.addEventListener('cheddar-open-options', h)
    return () => window.removeEventListener('cheddar-open-options', h)
  }, [openOptionsFor])
  useEffect(() => {
    const si = stkInfo(hlPair?.base); if (!si) return
    const u = si.opt ? si.sym.slice(0, 6).trim() : si.sym
    setOptChainFor(u); try { localStorage.setItem('cheddar_opt_underlying', u) } catch {}
  }, [hlPair?.base])

  // Data source effect — MUST live below the derivation above: its dep
  // array reads `dataSource` at render time, and that's a const now
  // (deriving from the active slot), so hoisting this effect earlier is
  // a temporal-dead-zone crash. Fires on focus switches too, which is
  // correct — listeners care about the ACTIVE source.
  useEffect(() => {
    dataSourceRef.current = dataSource
    window.dispatchEvent(new CustomEvent('solcheddar-datasource', { detail: { dataSource } }))
  }, [dataSource])

  // ─── Tape shared state (watched wallets + trade-size filter) ───
  // Lifted to App because both the Trade Tape panel and the chart consume it:
  // the filter feeds data.setTradeFilterMin, and the chart's Wallets tab (still
  // inside the chart until extracted) reads watchedWallets.
  const MAX_WATCHED_WALLETS = 10
  const [watchedWallets, setWatchedWallets] = useState(() => {
    try { return JSON.parse(localStorage.getItem('solcheddar_watchlist') || '[]') } catch { return [] }
  })
  const [walletInput, setWalletInput] = useState('')
  const [walletNameInput, setWalletNameInput] = useState('')
  const watchedWalletsRef = useRef(watchedWallets)
  const addWatchedWallet = useCallback(() => {
    const addr = walletInput.trim()
    if (!addr) return
    if (watchedWalletsRef.current.some(w => w.address === addr)) { showAppToast('Wallet already on list', 'sell'); return }
    if (watchedWalletsRef.current.length >= MAX_WATCHED_WALLETS) { showAppToast(`Max ${MAX_WATCHED_WALLETS} wallets`, 'sell'); return }
    const label = walletNameInput.trim()
    setWatchedWallets(prev => [...prev, { address: addr, label }])
    setWalletInput('')
    setWalletNameInput('')
  }, [walletInput, walletNameInput, showAppToast])
  useEffect(() => {
    watchedWalletsRef.current = watchedWallets
    try { localStorage.setItem('solcheddar_watchlist', JSON.stringify(watchedWallets)) } catch {}
    queuePush('watched_wallets', watchedWallets)
  }, [watchedWallets])

  // Grok app actions: follow / unfollow a wallet (grok-follow-wallet-20261019).
  // Same list + limits as the Wallets panel Add button. The ref is updated
  // right away so back-to-back calls dedupe correctly.
  useEffect(() => {
    const B58 = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/
    const short = (a) => `${a.slice(0, 4)}...${a.slice(-4)}`
    const offFollow = registerGrokAction('follow_wallet', {
      description: 'Add a Solana wallet to the user\'s followed wallets (Wallets panel). Optional label and alerts {buys: new token buys, xfers: transfers out}. Max ' + MAX_WATCHED_WALLETS + ' wallets.',
      params: {
        type: 'object',
        properties: {
          address: { type: 'string', description: 'base58 Solana wallet address' },
          label: { type: 'string', description: 'optional name, max 24 chars' },
          alerts: { type: 'object', properties: { buys: { type: 'boolean' }, xfers: { type: 'boolean' } } },
        },
        required: ['address'],
      },
      handler: async (a) => {
        const addr = String(a.address || '').trim()
        if (!B58.test(addr)) return { ok: false, error: 'not a valid Solana address' }
        const list = watchedWalletsRef.current
        if (list.some(w => w.address === addr)) return { ok: false, error: 'already followed' }
        if (list.length >= MAX_WATCHED_WALLETS) return { ok: false, error: `max ${MAX_WATCHED_WALLETS} wallets` }
        const label = typeof a.label === 'string' ? a.label.trim().slice(0, 24) : ''
        const entry = { address: addr, label }
        const al = a.alerts && typeof a.alerts === 'object' ? a.alerts : null
        if (al && (al.buys || al.xfers)) entry.alerts = { buys: !!al.buys, xfers: !!al.xfers }
        const next = [...list, entry]
        watchedWalletsRef.current = next
        setWatchedWallets(next)
        showAppToast(`Added ${short(addr)} to followed wallets`, 'buy')
        return { ok: true, message: `Added ${addr} to followed wallets`, count: next.length }
      },
    })
    const offUnfollow = registerGrokAction('unfollow_wallet', {
      description: 'Remove a wallet from the user\'s followed wallets by address.',
      params: { type: 'object', properties: { address: { type: 'string' } }, required: ['address'] },
      handler: async (a) => {
        const addr = String(a.address || '').trim()
        const list = watchedWalletsRef.current
        if (!list.some(w => w.address === addr)) return { ok: false, error: 'not in followed wallets' }
        const next = list.filter(w => w.address !== addr)
        watchedWalletsRef.current = next
        setWatchedWallets(next)
        showAppToast(`Removed ${short(addr)} from followed wallets`, 'sell')
        return { ok: true, message: `Removed ${addr} from followed wallets` }
      },
    })
    return () => { offFollow(); offUnfollow() }
  }, [showAppToast])

  const [tradeFilterMin, setTradeFilterMin] = useState(0)
  useEffect(() => { if (data.setTradeFilterMin) data.setTradeFilterMin(tradeFilterMin) }, [tradeFilterMin, data])
  // (chart2-bounce) Chart 2 gets the same tape filter. It used to be applied
  // to the main slot only, so chart 2's data layer ran unfiltered.
  useEffect(() => { if (slotB.data.setTradeFilterMin) slotB.data.setTradeFilterMin(tradeFilterMin) }, [tradeFilterMin, slotB.data])

  // ─── Trade history (Journal panel) ───
  const tradeHistory = useTradeHistory(publicKey)
  const hlFills = useHlFills({ enabled: dataSource === 'hyperliquid' })

  // ─── Token watchlist (★WL) — owned by App; the chart reads it for the
  // context-menu star + background enrichment, the WatchlistPanel renders it. ───
  // Bottom-ticker mirror: ScanPanel reports its scanTokens here while its
  // Ticker toggle is on (null otherwise / on unmount). Passed to the chart
  // as `tickerTokens` — the chart-resident ticker bar renders from this,
  // so scan updates only re-render the chart when the ticker is actually on.
  const [tickerTokens, setTickerTokens] = useState(null)

  // Ticker across windows (same browser): the scan feeds the ticker in its
  // OWN window via onTickerFeed; a window with only a chart has no scan, so
  // its ticker was empty. The feeding window also posts the tokens on
  // 'cheddar-ticker', and windows without a feeding scan of their own show
  // those. A local scan always wins over a broadcast one.
  const tickerChRef = useRef(null)
  const localTickerRef = useRef(false)   // this window's scan is feeding
  useEffect(() => {
    let ch = null
    try { ch = new BroadcastChannel('cheddar-ticker') } catch { return }
    tickerChRef.current = ch
    ch.onmessage = (ev) => {
      if (localTickerRef.current) return
      setTickerTokens(ev.data?.tokens ?? null)
    }
    return () => { tickerChRef.current = null; try { ch.close() } catch {} }
  }, [])
  const onTickerFeed = useCallback((tokens) => {
    localTickerRef.current = !!tokens
    setTickerTokens(tokens)
    if (tokens) {
      try { tickerChRef.current?.postMessage({ tokens }) } catch {}
      return
    }
    // The scan's feed effect clears (null) then re-feeds on every update.
    // Only broadcast a clear that SURVIVES the tick, or other windows would
    // blank their ticker for a frame on every scan update.
    setTimeout(() => {
      if (!localTickerRef.current) { try { tickerChRef.current?.postMessage({ tokens: null }) } catch {} }
    }, 0)
  }, [])

  // Token watchlist - named groups (tabs). Storage shape v2:
  //   { v:2, activeId, groups:[{ id, name, tokens:[] }] }
  // Flat arrays from older builds migrate into a single "Main" group.
  // tokenWatchlist / setTokenWatchlist still expose ONLY the active group's
  // tokens so the chart star + enrichment keep their existing contract.
  // Icons already IN storage are healed (ipfs -> proxy) on hydrate + remote.
  const WL_MAX_PER_GROUP = 100
  const wlNewId = () => `g_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`
  const healTokens = (list) => (Array.isArray(list) ? list.map(t => (
    t && t.icon ? { ...t, icon: normalizeTokenImage(t.icon) } : t
  )).filter(Boolean) : [])
  const defaultWlStore = () => ({
    v: 2,
    activeId: 'g_main',
    groups: [{ id: 'g_main', name: 'Main', tokens: [] }],
  })
  const normalizeWlStore = (raw) => {
    if (!raw) return defaultWlStore()
    if (Array.isArray(raw)) {
      return { v: 2, activeId: 'g_main', groups: [{ id: 'g_main', name: 'Main', tokens: healTokens(raw) }] }
    }
    if (raw && typeof raw === 'object' && Array.isArray(raw.groups)) {
      const groups = raw.groups
        .filter(g => g && g.id)
        .map(g => ({
          id: String(g.id),
          name: String(g.name || 'Group').slice(0, 24) || 'Group',
          tokens: healTokens(g.tokens),
        }))
      if (groups.length === 0) return defaultWlStore()
      const activeId = groups.some(g => g.id === raw.activeId) ? raw.activeId : groups[0].id
      return { v: 2, activeId, groups }
    }
    return defaultWlStore()
  }
  const [wlStore, setWlStore] = useState(() => {
    try { return normalizeWlStore(JSON.parse(localStorage.getItem('solcheddar-tokenwatchlist'))) }
    catch { return defaultWlStore() }
  })
  const tokenWatchlist = useMemo(() => {
    const g = wlStore.groups.find(x => x.id === wlStore.activeId) || wlStore.groups[0]
    return g?.tokens || []
  }, [wlStore])
  // Chart / panel mutate the ACTIVE group only. Growth past WL_MAX_PER_GROUP
  // is refused (no silent truncate) - map/filter same-length updates still pass.
  const setTokenWatchlist = useCallback((updater) => {
    setWlStore(prev => {
      const gi = Math.max(0, prev.groups.findIndex(g => g.id === prev.activeId))
      const cur = prev.groups[gi]?.tokens || []
      const next = typeof updater === 'function' ? updater(cur) : updater
      if (!Array.isArray(next)) return prev
      if (next.length > WL_MAX_PER_GROUP && next.length > cur.length) {
        showAppToast(`Watchlist group full (${WL_MAX_PER_GROUP})`, 'sell')
        return prev
      }
      const tokens = next.length > WL_MAX_PER_GROUP ? next.slice(0, WL_MAX_PER_GROUP) : next
      const groups = prev.groups.slice()
      groups[gi] = { ...groups[gi], tokens }
      return { ...prev, groups }
    })
  }, [showAppToast])
  const onSelectWlGroup = useCallback((id) => {
    setWlStore(prev => prev.groups.some(g => g.id === id) ? { ...prev, activeId: id } : prev)
  }, [])
  const onAddWlGroup = useCallback(() => {
    const id = wlNewId()
    setWlStore(prev => ({
      ...prev,
      activeId: id,
      groups: [...prev.groups, { id, name: 'New', tokens: [] }],
    }))
    return id
  }, [])
  const onRenameWlGroup = useCallback((id, name) => {
    const n = String(name || '').trim().slice(0, 24)
    if (!n) return
    setWlStore(prev => ({
      ...prev,
      groups: prev.groups.map(g => g.id === id ? { ...g, name: n } : g),
    }))
  }, [])
  const onDeleteWlGroup = useCallback((id) => {
    setWlStore(prev => {
      if (prev.groups.length <= 1) return prev
      const groups = prev.groups.filter(g => g.id !== id)
      const activeId = prev.activeId === id ? groups[0].id : prev.activeId
      return { ...prev, groups, activeId }
    })
  }, [])
  useEffect(() => {
    try { localStorage.setItem('solcheddar-tokenwatchlist', JSON.stringify(wlStore)) } catch {}
    queuePush('token_watchlist', wlStore)
  }, [wlStore])

  // --- Cross-device sync (D1 via /api/settings) ---
  // localStorage stays the read path (OrderflowTrail polls the wallet list
  // as a bare array - format unchanged); this only decides which value it
  // holds. Per-key LWW; pulls on boot / tab-visible / network regain;
  // degrades to local-only when signed out or the Function isn't deployed.
  // token_watchlist may be a legacy flat array OR a v2 groups object.
  useEffect(() => initSettingsSync({
    keys: [
      { key: 'watched_wallets', storageKey: 'solcheddar_watchlist' },
      { key: 'token_watchlist', storageKey: 'solcheddar-tokenwatchlist' },
    ],
    onRemote: (key, data) => {
      if (key === 'watched_wallets') {
        if (!Array.isArray(data)) return
        setWatchedWallets(data)
        return
      }
      if (key === 'token_watchlist') {
        setWlStore(normalizeWlStore(data))
      }
    },
  }), [])

  // ─── Messages (alerts / DEX paid / orders) + lobby chat ───
  // Owned at app level so a single source feeds both the footer toggle and
  // an optional grid panel. addMessage is threaded down to the chart, which
  // pushes order/DEX/alert events into it.
  const MESSAGES_CAP = 200
  const [messages, setMessages] = useState([]) // [{id, text, kind, ts}]
  const [messagesOpen, setMessagesOpen] = useState(false)
  const [messagesUnread, setMessagesUnread] = useState(0)
  // Chat gets its OWN unread counter — the footer toggle wears two badges:
  // red = log (alerts/DEX/orders), green = lobby chat, each with its own
  // count, slightly offset so both peek when both are nonzero.
  const [chatUnread, setChatUnread] = useState(0)
  const messagesOpenRef = useRef(false)
  const addMessage = useCallback((text, kind = 'info', meta = null) => {
    if (!text) return
    setMessages(prev => {
      const next = [
        // meta carries optional { mint, name } so log rows (e.g. price
        // alerts) can render a clickable token link that switches charts
        // via onMintClick → subscribeToToken. Plain messages omit it.
        { id: `m_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`, text, kind, ts: Date.now(),
          mint: meta?.mint || null, name: meta?.name || null },
        ...prev,
      ]
      return next.length > MESSAGES_CAP ? next.slice(0, MESSAGES_CAP) : next
    })
    setMessagesUnread(u => (messagesOpenRef.current ? 0 : u + 1))
  }, [])
  useEffect(() => { messagesOpenRef.current = messagesOpen }, [messagesOpen])
  // trade-errlog-20261019: failed market buys/sells (useTrading) -> messages log.
  useEffect(() => {
    const onErr = (e) => {
      const d = e.detail || {}
      const amt = d.side === 'buy' ? `${d.amount} SOL` : `${d.amount}%`
      const slip = d.slippageBps != null ? ` · slippage ${d.slippageBps} bps (${(d.slippageBps / 100).toFixed(1)}%)` : ''
      const tok = d.token ? ` ${String(d.token).slice(0, 6)}…` : ''
      const sig = d.signature ? ` · tx ${d.signature}` : ''
      const det = d.detail ? ` — ${d.detail}` : ''
      addMessage(`❌ ${d.side === 'buy' ? 'BUY' : 'SELL'} failed${tok} ${amt}: ${d.reason}${slip}${sig}${det}`, 'alert', d.token ? { mint: d.token } : null)
    }
    window.addEventListener('solcheddar-trade-error', onErr)
    return () => window.removeEventListener('solcheddar-trade-error', onErr)
  }, [addMessage])

  // chatTab is now per-instance (each MessagesPanel owns its own tab + scroll),
  // so chat must stay active while the docked panel is mounted — it's always in
  // the workspace and should always have live chat data. onUnread still bumps
  // the badge for new chat messages; the visible instances clear it.
  // Armed chart, read at send time. @grok uses this. Lobby messages do not.
  const grokContextRef = useRef(() => ({}))
  grokContextRef.current = () => {
    const fut = dataSource === 'hyperliquid'
    const status = activeData.status || ''
    const symbol = fut
      ? (hlPair?.base || hlPair?.symbol || '')
      : (typeof status === 'string' && status.startsWith('token:') ? status.slice(6) : '')
    const mcap = activeData.stats?.marketCap
    return {
      token: fut ? (hlPair?.base || symbol || '') : (activeData.currentToken || ''),
      symbol,
      timeframe: activeSlot.timeInterval,
      price: typeof mcap === 'number' ? mcap : null,
      note: fut
        ? `${stkInfo(hlPair?.base) ? 'Schwab' : cmeInfo(hlPair?.base) ? 'Tick-Stream' : 'Hyperliquid'} ${symbol || 'pair'} on screen, ${activeSlot.timeInterval}s candles`
        : `Solana ${symbol || 'token'} on screen, ${activeSlot.timeInterval}s candles`,
    }
  }

  const chat = useLobbyChat({
    wsRef:    data.wsRef,
    status:   data.status,
    active:   true,
    onUnread: () => setChatUnread(u => u + 1),
    getChartContextRef: grokContextRef,
  })

  // Fetch Hyperliquid pairs when dropdown opens
  useEffect(() => {
    if (!showFuturesDropdown || hlPairs.length > 0) return
    const ws = data.wsRef?.current
    if (!ws || ws.readyState !== WebSocket.OPEN) return

    const handler = (event) => {
      try {
        const msg = JSON.parse(event.data)
        if (msg.type === 'hl_pairs') {
          setHlPairs(msg.pairs || [])
          ws.removeEventListener('message', handler)
        }
      } catch {}
    }
    ws.addEventListener('message', handler)
    ws.send(JSON.stringify({ action: 'hl_pairs' }))
    return () => ws.removeEventListener('message', handler)
  }, [showFuturesDropdown, hlPairs.length, data.wsRef])

  // Boosted mints, mirrored off the main data WS — the SAME feed ScanPanel
  // reads, so the footer card and the scan list can never disagree about
  // whether a coin is boosted. A ref, not state: the card reads it on open.
  const boostedMintsRef = useRef(new Set())
  useEffect(() => {
    const ws = data.wsRef?.current
    if (!ws) return
    const onMsg = (e) => {
      let m; try { m = JSON.parse(e.data) } catch { return }
      if (!m?.type) return
      if (m.type === 'boosts_list') {
        const s2 = new Set()
        for (const k of ['latest', 'top']) for (const b of (m[k] || [])) {
          const mm = b?.mint || b?.tokenAddress; if (mm) s2.add(mm)
        }
        boostedMintsRef.current = s2
      } else if (m.type === 'boost_new' && (m.mint || m.tokenAddress)) {
        boostedMintsRef.current.add(m.mint || m.tokenAddress)
      }
    }
    ws.addEventListener('message', onMsg)
    return () => ws.removeEventListener('message', onMsg)
  }, [data.wsRef])

  // Also fetch eagerly the moment we enter futures mode, so the inline
  // search bar has data ready without the user having to open the
  // FUTURES button dropdown first.
  useEffect(() => {
    if (dataSource !== 'hyperliquid' || hlPairs.length > 0) return
    const ws = data.wsRef?.current
    if (!ws || ws.readyState !== WebSocket.OPEN) return
    const handler = (event) => {
      try {
        const msg = JSON.parse(event.data)
        if (msg.type === 'hl_pairs') {
          setHlPairs(msg.pairs || [])
          ws.removeEventListener('message', handler)
        }
      } catch {}
    }
    ws.addEventListener('message', handler)
    ws.send(JSON.stringify({ action: 'hl_pairs' }))
    return () => ws.removeEventListener('message', handler)
  }, [dataSource, hlPairs.length, data.wsRef])

  // --- Token Input Handlers ---
  // Shared HL pair selection — used by FUTURES dropdown rows AND the
  // inline search-results dropdown driven by tokenInput.
  const selectHlPair = useCallback((pair) => {
    if (!pair) return
    // Everything targets the ACTIVE slot: its mode, its connection, its
    // bucket/interval. The other chart doesn't hear about any of it —
    // this was the "pair switch changes both charts" bug (global mode
    // props flowed into both charts).
    setHlPair(pair)
    setFutTab(stkInfo(pair.base) ? 'stk' : cmeInfo(pair.base) ? 'cme' : 'crypto')
    setDataSource('hyperliquid')
    activeData.tokenSupplyRef.current = 1
    activeData.resetChart()
    const base = hlCoinNorm(pair.base)
    let initBucket = 0.1
    if (cmeInfo(base)) initBucket = cmeInfo(base).tick
    else if (stkInfo(base)) initBucket = 0.01
    else if (base === 'BTC') initBucket = 10
    else if (base === 'ETH') initBucket = 1
    activeSlot.setMcapBucketSize(initBucket)
    activeData.loadFuturesHistory(base, initBucket, activeSlot.timeInterval)
    if (activeData.subscribeHyperliquid) activeData.subscribeHyperliquid(base)
    const nextRecent = [pair, ...hlRecentPairs.filter(r => r.symbol !== pair.symbol)].slice(0, 6)
    setHlRecentPairs(nextRecent)
    try { localStorage.setItem('hl_recent_pairs', JSON.stringify(nextRecent)) } catch {}
    setShowFuturesDropdown(false)
    setTokenInput('')           // clear search box after pick
    setSearchHlHighlight(0)
    setSearchSlideOpen(false)
  }, [activeData, activeSlot, hlRecentPairs, setHlPair, setDataSource])

  // Resolve a bare coin ('ETH') to a pair object and switch through the ONE
  // futures path (selectHlPair). Lookup: live hlPairs list -> recent pairs ->
  // a minimal synthesized pair (base only) so a fut pick from the chip
  // dropdown / watchlist / boot restore works even before hlPairs has been
  // lazily fetched — maxLeverage on the chip heals when the real list lands.
  const pickFutByCoin = useCallback((coin) => {
    const c = hlCoinNorm(coin)
    if (!c) return
    const pair = hlPairs.find(p => hlCoinNorm(p.base) === c)
      || hlRecentPairs.find(p => hlCoinNorm(p.base) === c)
      || { base: c, symbol: c }
    selectHlPair(pair)
  }, [hlPairs, hlRecentPairs, selectHlPair])

  // Heal a synthesized pair. pickFutByCoin can select a bare {base} before
  // the lazy hlPairs fetch lands; the eager fetch fires on entering futures,
  // but nothing upgraded the slot's pair when the list arrived — so
  // szDecimals/maxLeverage stayed absent and order sizing fell back to 4
  // decimals, which Hyperliquid rejects as "invalid size" on coins with
  // fewer size decimals. Upgrade IN PLACE: same base, metadata only — no
  // resubscribe, no history reload, chip leverage and sizing both heal.
  // (chart2-bounce) EACH slot, not just the armed one. Healing only the active
  // pair left chart 2's futures pair without szDecimals until you clicked it;
  // the click then healed it, the Y tick floor changed, and its Y re-fit.
  useEffect(() => {
    if (hlPairs.length === 0) return
    for (const s of [slot, slotB]) {
      const cur = s.hlPair
      if (!cur?.base || cur.szDecimals != null) continue
      const real = hlPairs.find(p => p.base?.toUpperCase() === cur.base.toUpperCase())
      if (real) s.setHlPair(real)
    }
  }, [hlPairs, slot.hlPair, slotB.hlPair, slot.setHlPair, slotB.setHlPair]) // eslint-disable-line react-hooks/exhaustive-deps

  // Inline search-dropdown UI state. searchHlOpen flips false on Esc /
  // outside-click / selection; flips back on next keystroke.
  const [searchHlOpen, setSearchHlOpen] = useState(true)
  const [searchHlHighlight, setSearchHlHighlight] = useState(0)

  // Solana token search (Jupiter v2). Mirrors searchHlResults but for
  // sol mode — sourced from a network call instead of a local list, so
  // it needs loading state + abort handling.
  const [searchSolResults, setSearchSolResults] = useState([])
  const [searchSolLoading, setSearchSolLoading] = useState(false)
  const searchSolAbortRef = useRef(null)
  const searchSolDebounceRef = useRef(0)

  // Debounced Jupiter search. Re-runs on every keystroke but the actual
  // fetch is deferred 220ms — if the user keeps typing, the previous
  // timeout is cleared and any inflight request is aborted via the
  // AbortController stashed in searchSolAbortRef. Guarantees the latest
  // query wins even if earlier responses come back slow.
  //
  // Skipped in futures mode (HL has its own local-list search above)
  // and during typewriter paste animation (about to subscribe directly,
  // search would just race and lose).
  useEffect(() => {
    if (dataSource !== 'solana' || showFuturesDropdown) {
      setSearchSolResults([])
      setSearchSolLoading(false)
      return
    }
    const q = tokenInput.trim()
    if (q.length < 2 || typewriterActiveRef.current) {
      setSearchSolResults([])
      setSearchSolLoading(false)
      if (searchSolAbortRef.current) searchSolAbortRef.current.abort()
      return
    }
    setSearchHlHighlight(0)
    clearTimeout(searchSolDebounceRef.current)
    searchSolDebounceRef.current = setTimeout(() => {
      if (searchSolAbortRef.current) searchSolAbortRef.current.abort()
      const ctrl = new AbortController()
      searchSolAbortRef.current = ctrl
      setSearchSolLoading(true)
      searchTokens(q, { signal: ctrl.signal, limit: 12 })
        .then(rs => {
          if (ctrl.signal.aborted) return
          setSearchSolResults(rs)
          setSearchSolLoading(false)
        })
        .catch(() => {
          if (ctrl.signal.aborted) return
          setSearchSolResults([])
          setSearchSolLoading(false)
        })
    }, 220)
    return () => clearTimeout(searchSolDebounceRef.current)
  }, [dataSource, showFuturesDropdown, tokenInput])

  // Solana pick handler — analogous to selectHlPair. Routes through
  // subscribeToToken so the futures→solana mode flip and chart reset
  // happen in one path. Plain arrow (not useCallback) because
  // subscribeToToken is declared later in this component; referencing it
  // in a useCallback deps array here would hit the temporal dead zone.
  // It's only used in click handlers anyway — no memoization needed.
  const selectSolToken = (token) => {
    if (!token?.id) return
    subscribeToToken(token.id)
    setTokenInput('')
    setSearchSolResults([])
    setSearchHlOpen(false)
    setShowTokenHistory(false)
    setSearchHlHighlight(0)
    setSearchSlideOpen(false)
  }

  // Ranked filter of HL pairs by current tokenInput. Re-sorts as the
  // user types: exact base-match first, then starts-with, then contains.
  // Within each tier, recents are bumped up so frequently-traded pairs
  // surface first. Only computed when in futures mode + non-empty query
  // — sol mode returns [] so the dropdown stays closed.
  // ctxsearch-20261103: the search box searches whichever market tab is selected.
  const [stkLookup, setStkLookup] = useState(null)   // {q, pair|null, loading}
  const marketPairs = useMemo(() => {
    if (futTab === 'cme') return Object.entries(CME_FUTURES).map(([sym, info]) => ({ base: sym, symbol: sym, cme: true, name: info.name }))
    if (futTab === 'stk') return Object.entries(STOCKS).map(([sym, name]) => ({ base: 'STK:' + sym, symbol: 'STK:' + sym, stk: true, ticker: sym, name }))
    return hlPairs
  }, [futTab, hlPairs])
  useEffect(() => {
    const q = tokenInput.trim().toUpperCase()
    if (futTab !== 'stk' || !/^[A-Z][A-Z0-9./]{0,9}$/.test(q) || STOCKS[q]) { setStkLookup(null); return }
    setStkLookup({ q, pair: null, loading: true })
    let dead = false
    const t = setTimeout(() => {
      fetch(`${import.meta.env.VITE_HTTP_PROXY_BASE || 'https://history.cheddarterminal.app'}/stk/search?q=${encodeURIComponent(q)}`)
        .then(r => r.json()).then(j => {
          if (dead) return
          const x = j && j.result
          setStkLookup({ q, loading: false, error: j && j.error, pair: x ? { base: x.coin, symbol: x.coin, stk: true, ticker: x.symbol, name: x.description || x.symbol, last: x.last } : null })
        }).catch(() => { if (!dead) setStkLookup({ q, pair: null, loading: false, error: 'lookup failed' }) })
    }, 350)
    return () => { dead = true; clearTimeout(t) }
  }, [tokenInput, futTab])
  const searchHlResults = useMemo(() => {
    if (dataSource !== 'hyperliquid' && !showFuturesDropdown) return []
    const q = tokenInput.trim().toUpperCase()
    if (!q) return []
    const recentSet = new Set(hlRecentPairs.map(r => r.symbol))
    const tiers = [[], [], []]   // [exact, startsWith, contains]
    for (const p of marketPairs) {
      const base = (p.ticker || p.base || '').toUpperCase()
      const sym  = (p.symbol || '').toUpperCase()
      const nm   = (p.name || '').toUpperCase()
      if (base === q || sym === q)                           tiers[0].push(p)
      else if (base.startsWith(q) || sym.startsWith(q))      tiers[1].push(p)
      else if (base.includes(q) || sym.includes(q) || (q.length >= 3 && nm.includes(q))) tiers[2].push(p)
    }
    if (futTab === 'stk' && stkLookup?.pair && stkLookup.q === q) tiers[0].unshift(stkLookup.pair)
    // Recents bumped within each tier
    for (const tier of tiers) {
      tier.sort((a, b) => Number(recentSet.has(b.symbol)) - Number(recentSet.has(a.symbol)))
    }
    return [...tiers[0], ...tiers[1], ...tiers[2]].slice(0, 30)
  }, [dataSource, showFuturesDropdown, tokenInput, marketPairs, hlRecentPairs, futTab, stkLookup])

  const subscribeToToken = useCallback((address) => {
    if (!address) return
    if (dataSourceRef.current === 'hyperliquid') {
      setHlPair(null)
      setDataSource('solana')
      activeData.tokenSupplyRef.current = 1000000000
      const ws = activeData.wsRef?.current
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ action: 'hl_unsubscribe' }))
      }
    }
    // Charts the token on whichever chart is ARMED.
    activeData.handleSubscribe(address)
  }, [activeData])
  // (scrollperf) Stable identity for ScanPanel (now React.memo).
  // subscribeToToken re-creates on every activeData change (every trade tick),
  // which would defeat the memo; this forwards to the latest one, so a click
  // does exactly what it did before.
  const subscribeToTokenRef = useRef(subscribeToToken)
  subscribeToTokenRef.current = subscribeToToken

  // ─── Followed-wallet alerts (follow-alerts-20261018) ───
  // Per-wallet bell in the Wallets panel (alerts.buys / alerts.xfers on each
  // entry). The server watches; this toasts + logs. Buy toast -> chart the
  // token; transfer toast -> wallet map of the destination, + Follow button.
  const followAlerts = useFollowAlerts({
    wsRef: data.wsRef,
    status: data.status,
    watchedWallets,
    addMessage,
  })
  const onWalletAlertClick = useCallback((n) => {
    if (n.kind === 'buy' && n.mint) subscribeToTokenRef.current(n.mint)
    else if (n.kind === 'xfer' && n.to) openWalletMap(n.to)
    followAlerts.dismiss(n.id)
  }, [followAlerts.dismiss]) // eslint-disable-line react-hooks/exhaustive-deps
  const followWalletFromAlert = useCallback((addr) => {
    if (!addr) return
    if (watchedWalletsRef.current.some(w => w.address === addr)) { showAppToast('Wallet already on list', 'sell'); return }
    if (watchedWalletsRef.current.length >= MAX_WATCHED_WALLETS) { showAppToast(`Max ${MAX_WATCHED_WALLETS} wallets`, 'sell'); return }
    setWatchedWallets(prev => [...prev, { address: addr, label: '' }])
    showAppToast('Wallet followed', 'buy')
  }, [showAppToast])
  // ─── Scan → chart across windows (same browser) ───
  // A scan pick switches THIS window and is posted on 'cheddar-focus'; every
  // other open cheddar window switches its chart too, so the scan can live in
  // one window and the chart in another. Receivers don't re-post (no echo).
  const pickFutByCoinRef = useRef(null)
  pickFutByCoinRef.current = pickFutByCoin
  const focusChRef = useRef(null)
  useEffect(() => {
    let ch = null
    try { ch = new BroadcastChannel('cheddar-focus') } catch { return }
    focusChRef.current = ch
    ch.onmessage = (ev) => {
      const m = ev.data || {}
      if (m.kind === 'sol' && m.mint) subscribeToTokenRef.current(m.mint)
      else if (m.kind === 'fut' && m.coin) pickFutByCoinRef.current?.(m.coin)
    }
    return () => { focusChRef.current = null; try { ch.close() } catch {} }
  }, [])
  const onScanSelect = useCallback((address) => {
    subscribeToTokenRef.current(address)
    try { focusChRef.current?.postMessage({ kind: 'sol', mint: address }) } catch {}
  }, [])
  const onScanSelectFut = useCallback((coin) => {
    pickFutByCoinRef.current?.(coin)
    try { focusChRef.current?.postMessage({ kind: 'fut', coin }) } catch {}
  }, [])

  // ─── Deep links: /main/<mint> ──────────────────────────────────────
  // A Discord ping (or anyone) can link straight to a chart. Two behaviors:
  //  • If ANOTHER cheddar tab is already open and LIVE, this fresh tab hands
  //    the mint to it over a BroadcastChannel, shows a "switched in your open
  //    terminal" card, and tries to close itself — the open terminal flips
  //    charts through the normal token-switch path: no reload, wallet stays
  //    unlocked.
  //  • If no live tab acks within the window, this tab claims the mint and
  //    boots straight onto that chart (URL beats the localStorage restore).
  // The path stays honest in reverse: every Solana token switch
  // replaceState()s the URL to /main/<mint>, so the address bar is always a
  // shareable deep link to whatever you're looking at.
  const [deepLinkMint] = useState(() => {
    try {
      const m = window.location.pathname.match(/^\/main\/([1-9A-HJ-NP-Za-km-z]{32,44})\/?$/)
      return m ? m[1] : null
    } catch { return null }
  })
  const [deepPhase, setDeepPhase] = useState(deepLinkMint ? 'probing' : 'idle') // probing → handed | claimed
  const deepConnectedRef = useRef(false)
  useEffect(() => { deepConnectedRef.current = slot.data.status === 'connected' }, [slot.data.status])
  useEffect(() => {
    let ch = null
    try { ch = new BroadcastChannel('cheddar-deeplink') } catch {}
    if (!ch) { if (deepLinkMint) setDeepPhase('claimed'); return }  // no BC support → just claim
    const probeId = deepLinkMint ? Math.random().toString(36).slice(2) : null
    ch.onmessage = (ev) => {
      const msg = ev.data || {}
      if (msg.type === 'switch' && msg.mint) {
        // Only a LIVE terminal answers — a locked/booting tab stays silent so
        // the probing tab claims the mint itself (and unlocks there).
        if (!deepConnectedRef.current) return
        subscribeToToken(msg.mint)
        try { ch.postMessage({ type: 'ack', id: msg.id }) } catch {}
        try { window.focus() } catch {}
      } else if (msg.type === 'ack' && probeId && msg.id === probeId) {
        setDeepPhase('handed')
        // Best effort — browsers may refuse to close a link-opened tab, in
        // which case the handed-off card stays visible instead.
        setTimeout(() => { try { window.close() } catch {} }, 600)
      }
    }
    let t = null
    if (deepLinkMint) {
      try { ch.postMessage({ type: 'switch', mint: deepLinkMint, id: probeId }) } catch {}
      t = setTimeout(() => setDeepPhase(p => (p === 'probing' ? 'claimed' : p)), 700)
    }
    return () => { if (t) clearTimeout(t); try { ch.close() } catch {} }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Persist + restore the charted token across page refresh ───
  // The MAIN slot's Solana mint is remembered so a refresh drops you back on
  // the same chart instead of a blank input. HL futures state isn't persisted
  // here (it's pair-based, restored via its own path) — we only stash a real
  // Solana mint and only restore when booting into Solana mode (the default).
  const LAST_TOKEN_KEY = 'solcheddar_last_token'
  useEffect(() => {
    const tok = slot.data.currentToken
    if (!tok || tok.startsWith('hl_')) return
    if (slot.dataSource === 'hyperliquid') return
    try { localStorage.setItem(LAST_TOKEN_KEY, tok) } catch {}
  }, [slot.data.currentToken, slot.dataSource])

  // Mode-aware last view — the piece LAST_TOKEN_KEY never covered: futures.
  // {kind:'fut', coin} or {kind:'sol', address}; the boot restore below
  // checks this FIRST and reopens the pair through selectHlPair, so a
  // refresh on a futures chart lands back on that pair.
  const LAST_VIEW_KEY = 'cheddar_last_view'
  useEffect(() => {
    try {
      if (slot.dataSource === 'hyperliquid' && slot.hlPair?.base) {
        localStorage.setItem(LAST_VIEW_KEY, JSON.stringify({ kind: 'fut', coin: slot.hlPair.base }))
      } else if (slot.data.currentToken && !slot.data.currentToken.startsWith('hl_')) {
        localStorage.setItem(LAST_VIEW_KEY, JSON.stringify({ kind: 'sol', address: slot.data.currentToken }))
      }
    } catch {}
  }, [slot.dataSource, slot.hlPair?.base, slot.data.currentToken])

  // Address bar mirrors the charted Solana mint (/main/<mint>) — the URL is
  // always a shareable deep link to the current chart. replaceState, not push:
  // token switches shouldn't pollute back-button history.
  //
  // AND CLEARS IT IN FUTURES MODE. This effect used to just early-return on
  // hyperliquid, which left the last Solana mint sitting in the address bar
  // while a futures pair was on screen. On refresh that stale path parsed as
  // a deep link (deepLinkMint), deepPhase went 'claimed', and the boot
  // restore's `subscribeToToken(deepLinkMint); return` fired BEFORE the
  // cheddar_last_view check — so a refresh on futures always landed on
  // Solana. It looked browser-specific (fine on a phone, broken on desktop)
  // purely because the phone tab's URL was still '/', never having charted a
  // mint. Clearing the path restores the stated invariant — the URL
  // describes the CURRENT chart — and the deep-link path then only claims on
  // a genuine navigation.
  useEffect(() => {
    if (slot.dataSource === 'hyperliquid') {
      try {
        if (/^\/main\/.+/.test(window.location.pathname)) {
          window.history.replaceState({}, '', '/')
        }
      } catch {}
      return
    }
    const tok = slot.data.currentToken
    if (!tok || tok.startsWith('hl_')) return
    try { window.history.replaceState({}, '', '/main/' + tok) } catch {}
  }, [slot.data.currentToken, slot.dataSource])

  // Boot restore — re-subscribes the saved mint AND opens the view zoomed out
  // past the chart's candle-mode threshold (CANDLE_MODE_THRESHOLD = 100), so a
  // refresh lands on a scaled-out candle view. subscribeToToken forces Solana
  // mode and charts on the armed (main, on boot) slot.
  //
  // MUST wait for the socket: firing on bare mount raced the WebSocket open and
  // the subscribe hit a dead socket ("WebSocket not connected" toast). Gate on
  // slot.data.status === 'connected' and keep `status` in the deps — the socket
  // isn't ready at first render, and (documented elsewhere) an effect keyed only
  // on wsRef never re-fires when the socket actually comes up; status is the
  // signal that does. The ref makes it one-shot: it flips true the first time we
  // see a live socket, so a later reconnect (status cycling back to 'connected')
  // never re-charts the token out from under the user.
  const bootRestoredRef = useRef(false)
  useEffect(() => {
    if (bootRestoredRef.current) return
    if (slot.data.status !== 'connected') return   // wait for the socket
    if (deepPhase === 'probing') return            // deep-link handshake in flight — hold the restore
    bootRestoredRef.current = true
    if (deepPhase === 'handed') return             // another terminal took the mint; this tab is closing
    // Zoom out AND set candle mode explicitly. The chart's threshold effect is
    // crossing-only (its first observation just records the side, and it
    // respects persisted chartMode on load), so a wide boot window alone
    // wouldn't flip it. Drive it directly: 160 candles clears the 100 threshold
    // with margin (within MAX_VIEW_WINDOW*2); setChartMode pins candle so the
    // restored data paints scaled-out candles immediately.
    // (xzoom) A stored user zoom wins over the 160 overview; the mode then
    // follows the chart's own threshold (100) for that count.
    const _xWin = loadXWin('main')
    let _tpoSaved = false; try { _tpoSaved = localStorage.getItem('solcheddar_chart_mode') === 'tpo' } catch {}
    if (_tpoSaved) {   // tpo-20261103: TPO persists across reload
      setViewWindowSize(_xWin != null ? _xWin : 160)
      slot.setChartMode('tpo')
    } else if (_xWin != null) {
      setViewWindowSize(_xWin)
      slot.setChartMode(_xWin >= 100 ? 'candle' : 'footprint')
    } else {
      setViewWindowSize(160)
      slot.setChartMode('candle')
    }
    // Deep link claims priority over the remembered token — the URL is intent.
    if (deepPhase === 'claimed' && deepLinkMint) { subscribeToToken(deepLinkMint); return }
    // Futures restore: last view was a pair -> reopen it through the one
    // futures path (bucket/history/subscribe all owned by selectHlPair);
    // the candle-mode boot above still applies. Sol falls through to the
    // legacy LAST_TOKEN path unchanged.
    try {
      const lv = JSON.parse(localStorage.getItem(LAST_VIEW_KEY) || 'null')
      if (lv?.kind === 'fut' && lv.coin) { pickFutByCoin(lv.coin); return }
    } catch {}
    let saved = null
    try { saved = localStorage.getItem(LAST_TOKEN_KEY) } catch {}
    if (!saved || saved.startsWith('hl_')) return
    subscribeToToken(saved)
  }, [slot.data.status, deepPhase]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubscribe = () => {
    // Don't fire while the typewriter is still animating a partial mint
    if (typewriterActiveRef.current) return
    const q = tokenInput.trim()
    // In futures mode, Enter picks the highlighted (or first) HL pair
    // from the inline search results instead of trying to subscribe to
    // a Solana mint that doesn't exist.
    if (dataSourceRef.current === 'hyperliquid' || showFuturesDropdown || (q && hlPairs.length > 0 && searchHlResults.length > 0 && q.length <= 12)) {
      const pick = searchHlResults[searchHlHighlight] || searchHlResults[0]
      if (pick) {
        selectHlPair(pick)
        return
      }
    }
    // In Solana mode, Enter on a partial symbol picks the highlighted
    // Jupiter search result. Full-mint paste skips this path because
    // mints are 32-44 chars (length cutoff at 30). subscribeToToken is
    // still the fallback for full mints, manual paste, no-results, etc.
    if (dataSourceRef.current === 'solana' && q.length > 0 && q.length <= 30 && searchSolResults.length > 0) {
      const pick = searchSolResults[searchHlHighlight] || searchSolResults[0]
      if (pick) {
        selectSolToken(pick)
        return
      }
    }
    subscribeToToken(q)
  }

  // Shared onComplete for every typewriter path
  const handleTypewriterComplete = useCallback((text) => {
    typewriterActiveRef.current = false
    subscribeToToken(text.trim())
    setSearchSlideOpen(false)
  }, [subscribeToToken])

  // Fires on native Ctrl+V / Cmd+V / right-click-paste inside the input
  const handleTypewriterPaste = useTypewriterPaste(setTokenInput, {
    onComplete: handleTypewriterComplete,
  })

  // Paste button — reads clipboard via async API, then runs the same animation
  const handlePaste = async () => {
    try {
      const text = (await navigator.clipboard.readText()).trim()
      if (!text) return
      typewriterActiveRef.current = true
      typewriterType(setTokenInput, text, { onComplete: handleTypewriterComplete })
    } catch {
      showAppToast('Unable to read clipboard', 'sell')
    }
  }

  // Wrap the input's onPaste so we can flip the guard before the animation starts
  const handleInputPaste = (e) => {
    typewriterActiveRef.current = true
    handleTypewriterPaste(e)
  }

  // useCallback: this is passed to FootprintChart as `onHistorySelect`. As a
  // bare function it got a fresh identity on every App render, which defeated
  // memoization of the chart. All setters here are useState setters (stable by
  // React guarantee) and subscribeToToken is itself useCallback'd, so the only
  // real dependency is subscribeToToken.
  // Watchlist rows pass an address; futures rows use the stable
  // pseudo-address `fut:<coin>` (see the chart star / WatchlistPanel).
  const handleWatchlistSelect = useCallback((addr) => {
    if (typeof addr === 'string' && addr.startsWith('fut:')) pickFutByCoin(addr.slice(4))
    else subscribeToToken(addr)
  }, [pickFutByCoin, subscribeToToken])

  const handleHistorySelect = useCallback((entry) => {
    // Fut rows from the unified chip dropdown carry {kind:'fut', coin} —
    // route through the one futures path; the scrub below applies to both.
    if (entry?.kind === 'fut' && entry.coin) {
      pickFutByCoin(entry.coin)
    } else {
    // Match the Jupiter-scan pick path (selectSolToken) EXACTLY: subscribe,
    // then scrub all search/dropdown state. The old version set the input to
    // the full mint and left searchSolResults / searchHlHighlight dirty, so
    // the debounced Jupiter search could re-fire on that address and the
    // dropdown could repopulate/flicker on top of the chart switch — which is
    // why picking from history felt worse than picking from the scan. Clearing
    // the input + results makes the two paths behave identically.
    subscribeToToken(entry.address)
    }
    setTokenInput('')
    setSearchSolResults([])
    setSearchHlOpen(false)
    setShowTokenHistory(false)
    setSearchHlHighlight(0)
    setSearchSlideOpen(false)
  }, [subscribeToToken, pickFutByCoin])

  // Navigate to token from WalletHoldings click
  useEffect(() => {
    const handler = (e) => {
      const addr = e.detail?.address
      if (addr) {
        setTokenInput(addr)
        subscribeToToken(addr)
      }
    }
    window.addEventListener('solcheddar-navigate-token', handler)
    return () => window.removeEventListener('solcheddar-navigate-token', handler)
  }, [subscribeToToken])
  
  // Close dropdowns on click outside
  useEffect(() => {
    const handler = (e) => {
      if (inputWrapperRef.current && !inputWrapperRef.current.contains(e.target)) {
        setShowTokenHistory(false)
        setShowFuturesDropdown(false)
        setSearchHlOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const isFutures = dataSource === 'hyperliquid'

  return (
    <TradingProvider
      publicKey={publicKey}
      signTransaction={signTransaction}
      currentToken={activeData.currentToken}
      defaults={defaults}
      showToast={showAppToast}
      solPriceRef={activeData.solPriceRef}
      lastBucketMcapRef={activeData.lastBucketMcapRef}
      tokenSupplyRef={activeData.tokenSupplyRef}
      rawTradesRef={activeData.rawTradesRef}
      dataSource={dataSource}
      wsRef={activeData.wsRef}
      hlPair={hlPair}
    >
    {deepPhase === 'handed' && (
      <div style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'var(--app-bg, #0b0e14)', color: 'var(--text-hi, #e8eefc)',
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', gap: 10,
        fontFamily: "'JetBrains Mono', monospace", letterSpacing: '0.06em',
      }}>
        <div style={{ fontSize: 15 }}>SWITCHED IN YOUR OPEN TERMINAL 🧀</div>
        <div style={{ fontSize: 11, opacity: 0.6 }}>you can close this tab</div>
      </div>
    )}
    <div
      className="app"
      style={{
        // Installed-PWA safe areas. On a normal browser tab every inset is
        // 0px, so this is a no-op there. Once installed with
        // viewport-fit=cover (see index.html), iOS renders edge-to-edge under
        // the translucent status bar + home indicator and Android fullscreen
        // renders under display cutouts — these insets keep the header/footer
        // (and the chart between them) out of those zones. Left/right matter
        // in landscape, which is the orientation a chart gets rotated into.
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
        paddingLeft: 'env(safe-area-inset-left)',
        paddingRight: 'env(safe-area-inset-right)',
      }}
    >
      <header className={`header${searchSlideOpen ? ' search-slide-open' : ''}`} style={{ display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap' }}>
        <h1
          onClick={() => setSearchSlideOpen(o => !o)}
          style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: 0, flexShrink: 0, cursor: 'pointer' }}
        >
          <img src="/block.jpg" alt="Cheddar" style={{ height: '25px', width: '38px' }} />
          <span>Cheddar Terminal</span>
        </h1>
        
        {/* Above Chart Controls - Positioned to the right of "made in Wisconsin" */}
        <div className="above-chart" style={{ display: 'flex', gap: '15px', alignItems: 'center', flex: 1, justifyContent: 'flex-start' }}>
          <div className="input-wrapper" ref={inputWrapperRef}>
            <input
              type="text"
              inputMode="search"
              name="token-mint-input"
              id="token-mint-input"
              value={tokenInput}
              onChange={(e) => {
                setTokenInput(e.target.value)
                setSearchHlOpen(true)
                // Re-open the recents/Jupiter dropdown on every keystroke.
                // After picking a result, selectSolToken closes the
                // dropdown but the input often retains focus (the clicked
                // row isn't focusable), so onFocus doesn't re-fire on the
                // next keystroke. Without this, the dropdown stays
                // closed even though the user is actively typing.
                setShowTokenHistory(true)
                setSearchHlHighlight(0)
              }}
              onPaste={handleInputPaste}
              onKeyDown={(e) => {
                // Inline futures search keyboard nav
                if ((isFutures || showFuturesDropdown) && tokenInput && searchHlOpen && searchHlResults.length > 0) {
                  if (e.key === 'ArrowDown') {
                    e.preventDefault()
                    setSearchHlHighlight(i => Math.min(i + 1, searchHlResults.length - 1))
                    return
                  }
                  if (e.key === 'ArrowUp') {
                    e.preventDefault()
                    setSearchHlHighlight(i => Math.max(i - 1, 0))
                    return
                  }
                  if (e.key === 'Escape') {
                    setSearchHlOpen(false)
                    return
                  }
                }
                // Solana token search keyboard nav — same shape as futures,
                // shares the searchHlHighlight index (only one dropdown is
                // open at a time so there's no contention).
                if (!isFutures && !showFuturesDropdown && tokenInput && searchHlOpen && searchSolResults.length > 0) {
                  if (e.key === 'ArrowDown') {
                    e.preventDefault()
                    setSearchHlHighlight(i => Math.min(i + 1, searchSolResults.length - 1))
                    return
                  }
                  if (e.key === 'ArrowUp') {
                    e.preventDefault()
                    setSearchHlHighlight(i => Math.max(i - 1, 0))
                    return
                  }
                  if (e.key === 'Escape') {
                    setSearchHlOpen(false)
                    return
                  }
                }
                if (e.key === 'Enter') handleSubscribe()
              }}
              onFocus={() => { setShowTokenHistory(true); setSearchHlOpen(true) }}
              placeholder={isFutures ? (futTab === 'stk' ? 'Search stocks (any ticker: SPY, SPCX, TSM)...' : futTab === 'cme' ? 'Search CME (ES, NQ, GC, CL)...' : 'Search futures pairs (BTC, ETH, SOL)...') : 'Search tokens or paste mint address...'}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
              data-lpignore="true"
              data-form-type="other"
              data-1p-ignore="true"
              data-bwignore="true"
              aria-label="Token mint address"
            />
            <button className="paste-btn" onClick={handlePaste}>
              Paste
            </button>
            <button
              className="asset-dropdown-toggle"
              onClick={() => {
                const open = !showFuturesDropdown
                setShowFuturesDropdown(open)
                if (open) {
                  // Fresh futures search: drop Solana text/results and recents
                  setTokenInput('')
                  setSearchSolResults([])
                  setShowTokenHistory(false)
                  setSearchHlHighlight(0)
                  setSearchHlOpen(true)
                }
              }}
            >
              {dataSource === 'hyperliquid' && hlPair
                ? `FUTURES ${hlPair.base?.toUpperCase()}`
                : 'FUTURES'}
              <span className="dropdown-arrow">▼</span>
            </button>
            {/* Token History Dropdown — also hosts Jupiter token search
                results below the Recents section in Solana mode, so a
                single dropdown serves both. Outer gate opens when either
                section has content. */}
            {showTokenHistory && !showFuturesDropdown && !(isFutures && tokenInput.trim()) && (activeData.tokenHistory.length > 0 || (!isFutures && tokenInput.trim().length >= 2)) && (
              <div className="token-history-dropdown">
                {activeData.tokenHistory.length > 0 && (
                  <>
                    <div className="dropdown-section-header">Recent Tokens</div>
                    {activeData.tokenHistory.map((entry) => (
                      <div
                        key={entry.address}
                        className="token-history-item"
                        onClick={() => handleHistorySelect(entry)}
                      >
                        <span className="token-history-name">{entry.name}</span>
                        <span className="token-history-addr">
                          {entry.address.slice(0, 6)}...
                        </span>
                      </div>
                    ))}
                  </>
                )}
                {/* Jupiter results section. Three states (loading / empty /
                    results) all render the same dropdown frame so the
                    dropdown's height settles smoothly while typing. */}
                {!isFutures && tokenInput.trim().length >= 2 && (
                  <>
                    <div className="dropdown-section-header">
                      {searchSolLoading && searchSolResults.length === 0
                        ? 'Searching Jupiter…'
                        : searchSolResults.length > 0
                          ? `Jupiter · ${searchSolResults.length} match${searchSolResults.length === 1 ? '' : 'es'}`
                          : 'Jupiter'}
                    </div>
                    {!searchSolLoading && searchSolResults.length === 0 && (
                      <div className="token-history-item" style={{ cursor: 'default', justifyContent: 'center', color: '#666' }}>
                        No tokens match "{tokenInput}"
                      </div>
                    )}
                    {searchSolResults.map((t, i) => (
                      <div
                        key={t.id}
                        className={`token-history-item jup-search-item ${i === searchHlHighlight ? 'active' : ''}`}
                        onClick={() => selectSolToken(t)}
                        onMouseEnter={() => setSearchHlHighlight(i)}
                      >
                        <div className="jup-search-left">
                          {t.icon
                            ? <img className="jup-search-logo" src={t.icon} alt="" onError={(e) => { e.currentTarget.style.visibility = 'hidden' }} />
                            : <div className="jup-search-logo jup-search-logo-placeholder" />}
                          <div className="jup-search-text">
                            <span className="token-history-name">
                              {t.symbol || '?'}
                              {t.verified && <span className="jup-search-verified" title="Verified"> ✓</span>}
                            </span>
                            <span className="jup-search-name" title={t.name}>{t.name}</span>
                          </div>
                        </div>
                        <span className="token-history-addr">{fmtFdv(t.fdv)}</span>
                      </div>
                    ))}
                  </>
                )}
              </div>
            )}
            {/* Futures Dropdown */}
            {showFuturesDropdown && (
              <div className="unified-asset-dropdown">
                {/* Futures Section */}
                {(() => {
                  // Typing while the dropdown is open filters it (any mode).
                  if (tokenInput.trim()) {
                    if (marketPairs.length === 0) return <div className="dropdown-section-header">Loading pairs…</div>
                    if (searchHlResults.length === 0) return (
                      <div className="dropdown-section-header" style={{ padding: '12px', textAlign: 'center', color: '#666' }}>
                        {futTab === 'stk' && stkLookup?.loading ? `Looking up ${stkLookup.q} on Schwab…` : futTab === 'stk' && stkLookup?.error ? stkLookup.error : `No ${futTab === 'stk' ? 'stocks' : futTab === 'cme' ? 'CME symbols' : 'pairs'} match "${tokenInput}"`}
                      </div>
                    )
                    return (
                      <>
                        <div className="dropdown-section-header">
                          {futTab === 'stk' ? 'Stocks' : futTab === 'cme' ? 'CME' : 'Futures pairs'} · {searchHlResults.length} match{searchHlResults.length === 1 ? '' : 'es'}
                        </div>
                        {searchHlResults.map((pair, i) => (
                          <div
                            key={pair.symbol}
                            className={`futures-item ${i === searchHlHighlight ? 'active' : ''} ${hlPair?.symbol === pair.symbol ? 'active' : ''}`}
                            onClick={() => selectHlPair(pair)}
                            onMouseEnter={() => setSearchHlHighlight(i)}
                          >
                            <span className="futures-pair">{pair.ticker || pair.base}<span style={{ color: '#666' }}>{pair.stk || pair.cme ? ' ' + (pair.name || '') : '/USDC'}</span></span>
                            <span className="futures-meta">{pair.stk ? (STOCKS[pair.ticker] ? 'stock' : 'stock · on demand') : pair.cme ? 'CME' : `${pair.maxLeverage}x`}{pair.stk && <span style={{ cursor: 'pointer', textDecoration: 'underline', marginLeft: 6 }} onClick={(e) => { e.stopPropagation(); openOptionsFor(pair.base); setShowFuturesDropdown(false) }}>options</span>}</span>
                          </div>
                        ))}
                      </>
                    )
                  }
                  const recentSet = new Set(hlRecentPairs.map(r => r.symbol))
                  const recent = hlPairs.filter(p => recentSet.has(p.symbol))
                  const rest = hlPairs.filter(p => !recentSet.has(p.symbol))
                  const tabs = (
                    <div className="dropdown-section-header" style={{ display: 'flex', gap: 6, userSelect: 'none' }}>
                      <span onClick={(e) => { e.stopPropagation(); setFutTab('crypto') }} style={{ cursor: 'pointer', opacity: futTab === 'crypto' ? 1 : 0.45, fontWeight: futTab === 'crypto' ? 700 : 500 }}>Crypto</span>
                      <span style={{ opacity: 0.3 }}>|</span>
                      <span onClick={(e) => { e.stopPropagation(); setFutTab('cme') }} style={{ cursor: 'pointer', opacity: futTab === 'cme' ? 1 : 0.45, fontWeight: futTab === 'cme' ? 700 : 500 }}>CME</span>
                      <span style={{ opacity: 0.3 }}>|</span>
                      <span onClick={(e) => { e.stopPropagation(); setFutTab('stk') }} style={{ cursor: 'pointer', opacity: futTab === 'stk' ? 1 : 0.45, fontWeight: futTab === 'stk' ? 700 : 500 }}>Stocks</span>
                    </div>
                  )
                  if (futTab === 'stk') return (
                    <>
                      {tabs}
                      {!stkRelayUp && <div className="dropdown-section-header" style={{ color: '#ef4444' }}>Schwab relay offline</div>}
                      {hlRecentPairs.some(p => String(p.base || '').startsWith('STK')) && <div className="dropdown-section-header">Recent</div>}
                      {hlRecentPairs.filter(p => String(p.base || '').startsWith('STK')).map((p) => (
                        <div key={'r' + p.symbol} className={`futures-item ${hlPair?.base === p.base ? 'active' : ''}`} onClick={() => selectHlPair(p)}>
                          <span className="futures-pair">{stkInfo(p.base)?.sym || p.base}</span>
                          <span className="futures-meta" style={{ cursor: 'pointer', textDecoration: 'underline' }}
                            onClick={(e) => { e.stopPropagation(); openOptionsFor(p.base); setShowFuturesDropdown(false) }}>options</span>
                        </div>
                      ))}
                      {hlRecentPairs.some(p => String(p.base || '').startsWith('STK')) && <div className="dropdown-section-header">All stocks</div>}
                      {Object.entries(STOCKS).map(([sym, name]) => (
                        <div
                          key={sym}
                          className={`futures-item ${hlPair?.base === 'STK:' + sym ? 'active' : ''}`}
                          onClick={() => selectHlPair({ base: 'STK:' + sym, symbol: 'STK:' + sym, stk: true })}
                        >
                          <span className="futures-pair">{sym}<span style={{ color: '#666' }}> {name}</span></span>
                          <span className="futures-meta" style={{ cursor: 'pointer', textDecoration: 'underline' }}
                            onClick={(e) => { e.stopPropagation(); openOptionsFor(sym); setShowFuturesDropdown(false) }}>options</span>
                        </div>
                      ))}
                    </>
                  )
                  if (futTab === 'cme') return (
                    <>
                      {tabs}
                      {Object.entries(CME_FUTURES).map(([sym, info]) => (
                        <div
                          key={sym}
                          className={`futures-item ${hlPair?.base === sym ? 'active' : ''}`}
                          onClick={() => selectHlPair({ base: sym, symbol: sym, cme: true })}
                        >
                          <span className="futures-pair">{sym}<span style={{ color: '#666' }}> {info.name}</span></span>
                          <span className="futures-meta">live</span>
                        </div>
                      ))}
                    </>
                  )
                  return (
                    <>
                      {tabs}
                      {recent.length > 0 && (
                        <>
                          <div className="dropdown-section-header">Recent Pairs</div>
                          {recent.map((pair) => (
                            <div
                              key={pair.symbol}
                              className={`futures-item ${hlPair?.symbol === pair.symbol ? 'active' : ''}`}
                              onClick={() => selectHlPair(pair)}
                            >
                              <span className="futures-pair">{pair.base}<span style={{ color: '#666' }}>/USDC</span></span>
                              <span className="futures-meta">{pair.maxLeverage}x</span>
                            </div>
                          ))}
                        </>
                      )}
                      <div className="dropdown-section-header">All Pairs</div>
                      {rest.map((pair) => (
                        <div
                          key={pair.symbol}
                          className={`futures-item ${hlPair?.symbol === pair.symbol ? 'active' : ''}`}
                          onClick={() => selectHlPair(pair)}
                        >
                          <span className="futures-pair">{pair.base}<span style={{ color: '#666' }}>/USDC</span></span>
                          <span className="futures-meta">{pair.maxLeverage}x</span>
                        </div>
                      ))}
                    </>
                  )
                })()}
              </div>
            )}
            {/* Inline futures pair search — shows when in futures mode and
                user is typing into the main search bar. Resorts live; Up/Down
                navigates, Enter picks, Esc closes (or just clear input). */}
            {isFutures && tokenInput && searchHlOpen && !showFuturesDropdown && searchHlResults.length > 0 && (
              <div className="unified-asset-dropdown">
                <div className="dropdown-section-header">
                  {futTab === 'stk' ? 'Stocks' : futTab === 'cme' ? 'CME' : 'Futures pairs'} · {searchHlResults.length} match{searchHlResults.length === 1 ? '' : 'es'}
                </div>
                {searchHlResults.map((pair, i) => (
                  <div
                    key={pair.symbol}
                    className={`futures-item ${i === searchHlHighlight ? 'active' : ''} ${hlPair?.symbol === pair.symbol ? 'active' : ''}`}
                    onClick={() => selectHlPair(pair)}
                    onMouseEnter={() => setSearchHlHighlight(i)}
                  >
                    <span className="futures-pair">{pair.ticker || pair.base}<span style={{ color: '#666' }}>{pair.stk || pair.cme ? ' ' + (pair.name || '') : '/USDC'}</span></span>
                            <span className="futures-meta">{pair.stk ? (STOCKS[pair.ticker] ? 'stock' : 'stock · on demand') : pair.cme ? 'CME' : `${pair.maxLeverage}x`}{pair.stk && <span style={{ cursor: 'pointer', textDecoration: 'underline', marginLeft: 6 }} onClick={(e) => { e.stopPropagation(); openOptionsFor(pair.base); setShowFuturesDropdown(false) }}>options</span>}</span>
                  </div>
                ))}
              </div>
            )}
            {/* Empty-state hint when query has no matches */}
            {isFutures && tokenInput && searchHlOpen && !showFuturesDropdown && marketPairs.length > 0 && searchHlResults.length === 0 && (
              <div className="unified-asset-dropdown">
                <div className="dropdown-section-header" style={{ padding: '12px', textAlign: 'center', color: '#666' }}>
                  {futTab === 'stk' && stkLookup?.loading ? `Looking up ${stkLookup.q} on Schwab…` : futTab === 'stk' && stkLookup?.error ? stkLookup.error : `No ${futTab === 'stk' ? 'stocks' : futTab === 'cme' ? 'CME symbols' : 'pairs'} match "${tokenInput}"`}
                </div>
              </div>
            )}
            {/* Inline Solana token search results are now rendered inside
                the token-history-dropdown below the Recents section so
                there's a single dropdown instead of two stacking. */}
          </div>
        </div>

        <div style={{ marginLeft: 'auto' }} data-tour-id="wallet" data-tour-label="Wallet & trade station">
          <WalletArea stkRelayUp={stkRelayUp} dataSource={dataSource} isFutures={isFutures} hlPair={hlPair} data={activeData} stationOpen={stationOpen} setStationOpen={setStationOpen} stationCloseReq={stationCloseReq} />
        </div>
      </header>

      <Workspace
        panels={{
          chart: (
            // pointerDOWN-capture arms the chart BEFORE any click handler
            // fires — React re-renders between pointerdown and click, so
            // by the time a row-click/order handler runs, the trading
            // context already points at THIS chart. ChartHost forwards any
            // Workspace-injected coLocated/cellSwitch/headerless to the chart.
            <ChartHost
              hostActive={activeChartId === 'main'}
              chartId="main"
              onArm={() => setActiveChartId('main')}
              data={data}
              isActiveChart={activeChartId === 'main'}
              tickerTokens={tickerTokens}
              dataSource={slot.dataSource}
              setDataSource={slot.setDataSource}
              hlPair={slot.hlPair}
              setHlPair={slot.setHlPair}
              timeInterval={timeInterval}
              setTimeInterval={setTimeInterval}
              mcapBucketSize={mcapBucketSize}
              setMcapBucketSize={setMcapBucketSize}
              chartMode={slot.chartMode}
              setChartMode={slot.setChartMode}
              bucketDensity={slot.bucketDensity}
              setBucketDensity={slot.setBucketDensity}
              secondChart={secondChartForA}
              autoScrollEnabled={autoScrollEnabled}
              setAutoScrollEnabled={setAutoScrollEnabled}
              xWin={xWin}
              bodyScrollRef={bodyScrollRef}
              timeIntervalRef={timeIntervalRef}
              publicKeyRef={publicKeyRef}
              hlWallet={hlWallet}
              publicKey={publicKey}
              setTokenInput={setTokenInput}
              onHistorySelect={handleHistorySelect}
              theme={theme}
              themeId={themeId}
              setThemeId={setThemeId}
              addMessage={addMessage}
              tradeFilterMin={tradeFilterMin}
              tokenWatchlist={tokenWatchlist}
              setTokenWatchlist={setTokenWatchlist}
            />
          ),
          chart2: (
            <ChartHost
              hostActive={activeChartId === 'B'}
              chartId="B"
              onArm={() => setActiveChartId('B')}
              data={slotB.data}
              isActiveChart={activeChartId === 'B'}
              tickerTokens={null}
              secondChart={secondChartForB}
              dataSource={slotB.dataSource}
              setDataSource={slotB.setDataSource}
              hlPair={slotB.hlPair}
              setHlPair={slotB.setHlPair}
              timeInterval={slotB.timeInterval}
              setTimeInterval={slotB.setTimeInterval}
              mcapBucketSize={slotB.mcapBucketSize}
              setMcapBucketSize={slotB.setMcapBucketSize}
              chartMode={slotB.chartMode}
              setChartMode={slotB.setChartMode}
              bucketDensity={slotB.bucketDensity}
              setBucketDensity={slotB.setBucketDensity}
              autoScrollEnabled={slotB.autoScrollEnabled}
              setAutoScrollEnabled={slotB.setAutoScrollEnabled}
              xWin={slotB.xWin}
              bodyScrollRef={slotB.bodyScrollRef}
              timeIntervalRef={slotB.timeIntervalRef}
              publicKeyRef={publicKeyRef}
              hlWallet={hlWallet}
              publicKey={publicKey}
              setTokenInput={setTokenInput}
              onHistorySelect={handleHistorySelect}
              theme={theme}
              themeId={themeId}
              setThemeId={setThemeId}
              addMessage={addMessage}
              tradeFilterMin={tradeFilterMin}
              tokenWatchlist={tokenWatchlist}
              setTokenWatchlist={setTokenWatchlist}
            />
          ),
          scan: (
            <ScanPanel
              wsRef={data.wsRef}
              wsStatus={data.status}
              onSelect={onScanSelect}
              onSelectFut={onScanSelectFut}
              onTickerFeed={onTickerFeed}
              currentToken={activeData.currentToken}
              currentFut={hlPair?.base}
            />
          ),
          chat: (
            <MessagesPanel
              docked
              showToggle={false}
              messages={messages}
              setMessages={setMessages}
              setMessagesOpen={setMessagesOpen}
              messagesUnread={messagesUnread}
              setMessagesUnread={setMessagesUnread}
              chatUnread={chatUnread}
              setChatUnread={setChatUnread}
              chat={chat}
              onMintClick={subscribeToToken}
            />
          ),
          tape: (
            <TapePanel
              data={activeData}
              dataSource={dataSource}
              watchedWallets={watchedWallets}
              tradeFilterMin={tradeFilterMin}
              setTradeFilterMin={setTradeFilterMin}
            />
          ),
          wstats: (
            <WalletStatsPanel
              data={activeData}
              dataSource={dataSource}
              isCme={dataSource === 'hyperliquid' && !!cmeInfo(hlPair?.base)}
              watchedWallets={watchedWallets}
              publicKey={publicKey ? publicKey.toString() : null}
            />
          ),
          station: (
            // WalletHoldings trade controls as a panel — meant for phone (the
            // header trade row is awkward on a narrow screen). Mirrors the
            // per-datasource props the header WalletArea uses: futures passes
            // dataSource + hlPair, Solana passes neither. Registered in
            // PANEL_LABELS but hidden from the picker except on phone (Workspace).
            <div className="station-panel-host">
              {isFutures
                ? <WalletHoldings showToast={showAppToast} dataSource="hyperliquid" hlPair={hlPair} data={activeData} asPanel />
                : <WalletHoldings showToast={showAppToast} data={activeData} asPanel />}
            </div>
          ),
          wallets: (
            <WalletsPanel
              watchedWallets={watchedWallets}
              setWatchedWallets={setWatchedWallets}
              addWatchedWallet={addWatchedWallet}
              walletInput={walletInput}
              setWalletInput={setWalletInput}
              walletNameInput={walletNameInput}
              setWalletNameInput={setWalletNameInput}
            />
          ),
          portfolio: (
            <PortfolioPanel
              publicKey={publicKey}
              hlWallet={hlWallet}
              tradeHistory={tradeHistory}
              wsRef={activeData.wsRef}
              solPriceRef={activeData.solPriceRef}
              currentToken={activeData.currentToken}
              rawTradesRef={activeData.rawTradesRef}
              lastBucketMcapRef={activeData.lastBucketMcapRef}
              onSelectMint={onScanSelect}
              onSelectFut={onScanSelectFut}
            />
          ),
          journal: (
            <JournalPanel
              dataSource={dataSource}
              publicKey={publicKey}
              hlWallet={hlWallet}
              tradeHistory={tradeHistory}
              hlFills={hlFills}
              formatCompact={formatCompact}
              onSelectMint={subscribeToToken}
              showToast={showAppToast}
            />
          ),
          notes: (
            <NotesPanel />
          ),
          cal: (
            <CalendarPanel />
          ),
          opt: (
            <OptionsChainPanel underlying={optChainFor} activeCoin={hlPair?.base} relayUp={stkRelayUp}
              onPick={(c) => { const coin = 'STK-OPT:' + c.symbol; selectHlPair({ base: coin, symbol: coin, stk: true }) }} />
          ),
          wl: (
            <WatchlistPanel
              tokenWatchlist={tokenWatchlist}
              setTokenWatchlist={setTokenWatchlist}
              wlGroups={wlStore.groups}
              wlActiveId={wlStore.activeId}
              onSelectWlGroup={onSelectWlGroup}
              onAddWlGroup={onAddWlGroup}
              onRenameWlGroup={onRenameWlGroup}
              onDeleteWlGroup={onDeleteWlGroup}
              maxPerGroup={WL_MAX_PER_GROUP}
              onSelect={handleWatchlistSelect}
              currentToken={activeData.currentToken}
            />
          ),
        }}
      />

      <footer className="app-footer" data-tour-id="footer" data-tour-label="Footer">
        <div className="df-left">
        {/* Connection status — left. Follows the ACTIVE chart (activeData), so
            switching charts shows that chart's connection/token state. */}
        {dataSource === 'hyperliquid' ? (
          /* Futures used a HARDCODED "connected" class: permanently green no
             matter what the socket was doing, which is the opposite of what a
             status light is for. Same lamp, same real status — it is the same
             droplet connection behind both modes. */
          <span
            className={`status ${activeData.status === 'connected' || activeData.status.startsWith('token:') ? 'connected' : 'disconnected'}`}
            style={{ marginRight: 0 }}
            title={`Data connection: ${activeData.status}`}
          >
            <span
              className="status-lamp" onClick={openHealth}
              style={{
                display: 'inline-block',
                width: 7, height: 7, borderRadius: '50%',
                marginRight: 6, verticalAlign: 'middle', flexShrink: 0,
                background:
                  activeData.status === 'connected' || activeData.status.startsWith('token:') ? '#22c55e'
                    : activeData.status === 'max_retries_reached' ? '#ef4444'
                    : activeData.status.startsWith('reconnecting') ? '#f59e0b'
                    : '#6b7280',
                boxShadow:
                  activeData.status === 'connected' || activeData.status.startsWith('token:')
                    ? '0 0 5px rgba(34,197,94,0.8)'
                    : activeData.status.startsWith('reconnecting')
                    ? '0 0 5px rgba(245,158,11,0.8)'
                    : 'none',
                animation: activeData.status.startsWith('reconnecting') ? 'lampPulse 1s ease-in-out infinite' : undefined,
              }}
            />
            {stkInfo(hlPair?.base) ? (stkRelayUp ? 'Schwab' : 'Schwab relay offline') : cmeInfo(hlPair?.base) ? 'Tick-Stream' : 'Hyperliquid'}
          </span>
        ) : (() => {
          // The status ALREADY resolves to the token symbol whenever it reads
          // "token:…" — so it's only a button in that one state. Connected /
          // Disconnected / Reconnecting have no token behind them and clicking
          // them should do nothing rather than open an empty card.
          const onToken = activeData.status.startsWith('token:') && !!activeData.currentToken
          return (
            <span
              className={`status ${activeData.status === 'connected' || activeData.status.startsWith('token:') ? 'connected' : 'disconnected'}`}
              style={{
                marginRight: 0,
                cursor: onToken ? 'pointer' : undefined,
                textDecoration: onToken ? 'underline dotted' : undefined,
                textUnderlineOffset: onToken ? 3 : undefined,
              }}
              onClick={onToken ? () => setTokenCardOpen(o => !o) : undefined}
              title={onToken ? activeData.currentToken : undefined}
            >
              {/* Transport lamp. The status TEXT usually shows the token, so
                  the connection state was invisible exactly when it mattered
                  — a socket could be down while the footer read like a
                  healthy symbol. The dot always tells the truth about the
                  pipe: green live, amber reconnecting, grey offline, red
                  gave up. It also makes the gap-fill legible: fills fire on
                  the amber→green transition, so seeing the dot settle is
                  seeing the heal run. */}
              <span
                className="status-lamp" onClick={openHealth}
                title={`Data connection: ${activeData.status}`}
                style={{
                  display: 'inline-block',
                  width: 7, height: 7, borderRadius: '50%',
                  marginRight: 6, verticalAlign: 'middle', flexShrink: 0,
                  background:
                    activeData.status === 'connected' || activeData.status.startsWith('token:') ? '#22c55e'
                      : activeData.status === 'max_retries_reached' ? '#ef4444'
                      : activeData.status.startsWith('reconnecting') ? '#f59e0b'
                      : activeData.status === 'offline' ? '#6b7280'
                      : '#6b7280',
                  boxShadow:
                    activeData.status === 'connected' || activeData.status.startsWith('token:')
                      ? '0 0 5px rgba(34,197,94,0.8)'
                      : activeData.status.startsWith('reconnecting')
                      ? '0 0 5px rgba(245,158,11,0.8)'
                      : 'none',
                  animation: activeData.status.startsWith('reconnecting') ? 'lampPulse 1s ease-in-out infinite' : undefined,
                }}
              />
              {activeData.status === 'connected' ? 'Connected'
                : activeData.status.startsWith('token:') ? activeData.status.slice(6)
                : activeData.status === 'max_retries_reached' ? 'Connection Failed'
                : activeData.status.startsWith('reconnecting') ? activeData.status
                : activeData.status === 'offline' ? 'Offline'
                : 'Disconnected'}
            </span>
          )
        })()}

        {!isPhone && <span className="df-venue">{dataSource === 'hyperliquid' ? (stkInfo(hlPair?.base) ? (stkInfo(hlPair.base).opt ? 'STK-OPT' : 'STK') : cmeInfo(hlPair?.base) ? 'CME' : 'Perps') : 'Solana'}</span>}
        {dataSource === 'hyperliquid' && stkInfo(hlPair?.base) && (<>
          <span className="df-venue" style={{ color: stkRelayUp ? '#f59e0b' : '#ef4444' }} title="Schwab has no equity time & sales; prints are estimated from ~1s level-one volume changes">
            {stkRelayUp ? 'estimated footprint (~1s, no tape) · view only' : 'Schwab relay offline'}</span>
        </>)}
        </div>
        {!isPhone && <FooterTicker tokens={tickerTokens} onPick={subscribeToToken} />}

        {/* Right group: WALLET · CHAT · BRUSH — three peer toggles in ONE flex
            row with a single `gap`, so the spacing can't drift. They used to
            carry their own ad-hoc margins (chat: none, brush: 8 left / 8 right),
            which is exactly why they never lined up. One gap, one source of
            truth — add a fourth toggle and it spaces itself. */}
        <span style={{
          marginLeft: 'auto',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 3,
          height: 21,
          marginRight: 15,
        }}>
          {/* Trade station toggle. Cannot reuse .wallet-logo — that class is
              `display: none !important` on phone, which is the ONE place this
              button has to exist. Its own sizing, deliberately. */}
          <button
            onClick={() => (stationOpen ? setStationCloseReq(n => n + 1) : setStationOpen(true))}
            title={stationOpen ? 'Hide trade station' : 'Show trade station'}
            style={{
              background: 'transparent',
              border: 0,
              padding: 0,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              lineHeight: 1,
            }}
          >
            <img
              src="/Wallet1.png"
              alt="trade station"
              style={{
                width: 15,
                height: 15,
                display: 'block',
                // Optical, not layout. The boxes ARE aligned — Wallet1.png just
                // carries its visual mass low inside its own frame, so it reads
                // as sitting below chat and brush. transform (not margin) so the
                // correction is purely a paint offset and the flex row's metrics
                // stay honest.
                transform: 'translateY(-1px)',
                // Open = lit, closed = dimmed. The icon reads as state, not just
                // a button — same language as the rest of the footer.
                opacity: stationOpen ? 0.85 : 0.4,
                transition: 'opacity 0.15s',
              }}
            />
          </button>

          <MessagesPanel
            messages={messages}
            setMessages={setMessages}
            messagesOpen={messagesOpen}
            setMessagesOpen={setMessagesOpen}
            messagesUnread={messagesUnread}
            setMessagesUnread={setMessagesUnread}
            chatUnread={chatUnread}
            setChatUnread={setChatUnread}
            chat={chat}
            onMintClick={subscribeToToken}
          />

          {/* Chat ↔ brush runs 2px TIGHTER than the row gap — deliberate
              exception to the one-gap rule above (the brush glyph carries its
              ink toward the right of its frame, so the equal gap reads wider
              than it measures). A negative margin COMPOSES with the flex gap
              (3 − 2 = 1px) instead of replacing it: retune the row gap and
              this seam follows, still exactly 2px tighter. The wrapper span
              is unpositioned, so ThemePicker's popover anchoring is untouched. */}
          <span style={{ marginLeft: -2, display: 'inline-flex', alignItems: 'center' }}>
            {/* Expand: whole-app browser fullscreen (moved from the chart header). */}
            <button
              type="button"
              onClick={toggleFullscreen}
              title={isFullscreen ? 'Exit fullscreen' : 'Expand'}
              aria-label="Expand"
              style={{ height: 21, width: 21, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: 'transparent', border: 0, padding: 0, margin: 0, cursor: 'pointer', color: isFullscreen ? '#aaa' : '#666' }}
            >
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor"
                   strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ display: 'block' }}>
                <path d="M9 2.5H4A1.5 1.5 0 0 0 2.5 4v8A1.5 1.5 0 0 0 4 13.5h8a1.5 1.5 0 0 0 1.5-1.5V7" />
                <path d="M8 8l5.5-5.5M9.5 2.5h4v4" />
              </svg>
            </button>
            {/* Theme menu (renders nothing until chart right-click > Theme opens it at that point). */}
            <ThemePicker
              themeId={themeId}
              setThemeId={setThemeId}
              theme={theme}
              themeOverrides={themeOverrides}
              setThemeColor={setThemeColor}
              resetThemeColor={resetThemeColor}
              resetThemeColors={resetThemeColors}
              themesVersion={themesVersion}
              addTheme={addTheme}
              deleteTheme={deleteTheme}
            />
            {/* Chat settings control: theme + trade-default bindings, confirm card. */}
            <SettingsBridge
              themeId={themeId}
              setThemeId={setThemeId}
              themeOverrides={themeOverrides}
              setThemeColor={setThemeColor}
            />
          </span>
        </span>

        {/* Live LOCAL time — far right. No timezone, no gap: "6:55PM".
            Assembled from getHours()/getMinutes() rather than stripping the space
            out of toLocaleTimeString, because that separator is ICU-dependent:
            en-US emits a NARROW NO-BREAK SPACE (U+202F) before AM/PM on newer
            ICU, so a plain .replace(' ', '') silently no-ops on some engines
            while working fine on others. Building it ourselves has no locale
            surface to get wrong — and getHours() is local time by definition. */}
        <span style={{ fontFamily: 'monospace', color: '#888', fontSize: 11, whiteSpace: 'nowrap', marginRight: 8 }}>
          <FooterClock seconds={!isPhone} showTz={!isPhone} />
        </span>
      </footer>

      {/* Slides up from the footer. Chart a different token and the card must go
          with it — it's about the CHARTED coin, and leaving it showing the old
          one is worse than not showing it at all. */}
      {healthAt && <HealthPanel data={activeData} anchor={healthAt} onClose={() => setHealthAt(null)} />}
      {tokenCardOpen && activeData.currentToken && (
        <TokenCard
          key={activeData.currentToken}
          mint={activeData.currentToken}
          boosted={boostedMintsRef.current.has(activeData.currentToken)}
          onClose={() => setTokenCardOpen(false)}
        />
      )}

      {/* Any wallet's holdings + interaction map. Opened by openWalletMap()
          from the followed list, the tape and wallet stats. */}
      <WalletMapPanel
        watchedWallets={watchedWallets}
        setWatchedWallets={setWatchedWallets}
        showToast={showAppToast}
        maxWatched={MAX_WATCHED_WALLETS}
        currentMint={tokenInput}
      />

      <WalletAlertNotifications
        notifications={followAlerts.notifications}
        onClick={onWalletAlertClick}
        onDismiss={followAlerts.dismiss}
        onFollow={followWalletFromAlert}
      />
      <Toast message={appToast.message} type={appToast.type} show={appToast.show} />
    </div>
    </TradingProvider>
  )
}

function App() {
  const walletConfig = useMemo(() => ({
    autoConnect: true,
    env: 'mainnet-beta',
    metadata: {
      name: 'Cheddar',
      description: 'Real-time footprint chart for pump.fun tokens',
      url: 'https://cheddarterminal.app',
      iconUrls: ['https://cheddarterminal.app/cheese.png'],
    },
    notificationCallback: {
      onConnect: (props) => dbg('Wallet connected', props),
      onConnecting: (props) => dbg('Wallet connecting', props),
      onDisconnect: (props) => dbg('Wallet disconnected', props),
      onNotInstalled: (props) => dbg('Wallet not installed', props),
    },
    walletlistExplanation: {
      href: 'https://station.jup.ag/docs/additional-topics/wallet-list',
    },
    theme: 'dark',
    lang: 'en',
  }), [])

  return (
    <TradeDefaultsProvider>
    <UnifiedWalletProvider wallets={[]} config={walletConfig}>
    <LocalWalletProvider>
    <DualWalletProvider>
    <HyperliquidWalletProvider>
      <AppContent />
    </HyperliquidWalletProvider>
    </DualWalletProvider>
    </LocalWalletProvider>
    </UnifiedWalletProvider>
    </TradeDefaultsProvider>
  )
}

export default App